<template>
  <main id="create-quote-page" class="page-container">
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <VueFinalModal
      v-model="showDuplicateModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content dup-modal__content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="closeDuplicateModal">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <h3 class="modal__title">Preventivo già presente</h3>

        <p class="dup-modal__intro">
          Esiste già un preventivo per questo cliente allo stesso indirizzo in
          corso...
          <span
            v-if="lastDuplicateQuoteInfo?.address"
            class="dup-modal__address"
          >
            ({{ lastDuplicateQuoteInfo.address }}) </span
          >.
        </p>

        <!-- loading -->
        <div v-if="dupLoading" class="dup-modal__skeleton">
          Caricamento in corso…
        </div>
        <!-- error -->
        <div v-else-if="dupError" class="dup-modal__error">{{ dupError }}</div>

        <!-- dati -->
        <div v-else-if="dupQuote" class="dup-quote">
          <div class="dup-quote__grid">
            <div>
              <div class="dup-quote__label">ID</div>
              <div class="dup-quote__value">#{{ dupQuote.id }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Data</div>
              <div class="dup-quote__value">
                {{ formatToLocalDate(dupQuote.date) }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Cliente</div>
              <div class="dup-quote__value">
                {{ dupQuote.clientName || "—" }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Pagamento</div>
              <div class="dup-quote__value">{{ dupQuote.payment_method }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Indirizzo</div>
              <div class="dup-quote__value">
                <a
                  v-if="dupQuote.location?.link"
                  :href="dupQuote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                >
                  {{ dupQuote.location?.address || "Vedi mappa" }}
                </a>
                <span v-else>{{ dupQuote.location?.address || "—" }}</span>
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Totale</div>
              <div class="dup-quote__value">
                {{ money(dupQuote.totalPrice) }}
              </div>
            </div>
          </div>

          <div class="dup-quote__items">
            <div class="dup-quote__items-head">
              <strong>Riepilogo articoli</strong>
              <span class="muted">({{ dupQuote.items?.length || 0 }})</span>
            </div>
            <ul class="dup-quote__items-list">
              <li
                v-for="(it, idx) in dupQuote.items || []"
                :key="idx"
                class="dup-item"
              >
                <div class="dup-item__name">
                  {{ it.itemName || it.brand || "Articolo" }}
                </div>
                <div class="dup-item__meta">
                  <span v-if="it.category" class="chip">{{ it.category }}</span>
                  <span v-if="it.boxQuantity"
                    >scatole: {{ it.boxQuantity }}</span
                  >
                  <span v-if="it.pieces">pezzi: {{ it.pieces }}</span>
                  <span v-if="it.iva !== undefined">IVA: {{ it.iva }}%</span>
                  <span v-if="it.price !== undefined"
                    >€ {{ number(it.price) }}</span
                  >
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="dup-modal__footer">
          <button
            v-if="dupQuote && !dupLoading && dupDocuments.length"
            class="btn-primary"
            @click="showDupDocs = true"
          >
            Apri file
          </button>

          <button class="btn-primary" @click="onChangeAddress">
            Usa indirizzo diverso
          </button>

          <button
            class="btn-ghost"
            :disabled="!lastDuplicateQuoteInfo?.quoteId"
            @click="onDuplicateWithEdit"
          >
            Duplica modificando indirizzo
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modale documenti PDF del preventivo duplicato -->
    <VueFinalModal
      v-model="showDupDocs"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="documents-modal-content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="showDupDocs = false">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Preventivo #{{ dupQuote?.id }}</h3>

        <div class="documents-grid">
          <div v-for="doc in dupDocuments" :key="doc.id" class="document-card">
            <a href="#" class="card-link" @click.prevent="openDoc(doc)">
              <div class="card-icon-pdf">
                <i class="fa-solid fa-file-pdf"></i>
              </div>
              <div class="card-body">
                <div class="card-title">{{ formatDocName(doc.name) }}</div>
                <div class="card-subtitle">
                  Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                </div>
              </div>
            </a>
          </div>
          <div v-if="dupDocuments.length === 0" class="no-result">
            Nessun documento trovato
          </div>
        </div>
      </template>
    </VueFinalModal>

    <!-- MODALE CONFERMA INVIO EMAIL [sc-48246]
         "Invia email al cliente" partiva al primo click. Qui il rischio è
         maggiore che negli elenchi: si sta modificando il documento, e un
         click di troppo manda al cliente una versione non definitiva. -->
    <VueFinalModal
      v-model="showEmailModal"
      :clickToClose="!sendingEmail"
      :escToClose="!sendingEmail"
      classes="documents-modal"
      content-class="confirm-modal-content modal--state"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="closeEmailModal"
          :disabled="sendingEmail"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Invia email al cliente</h3>
        <p class="modal__subtitle" v-html="emailConfirmMessage"></p>
        <div class="modal-actions">
          <button
            class="secondary-btn"
            @click="closeEmailModal"
            :disabled="sendingEmail"
          >
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmSendDocumentEmail"
            :disabled="sendingEmail || !emailRecipient"
          >
            <span v-if="sendingEmail">Invio…</span>
            <span v-else>Invia email</span>
          </button>
        </div>
      </template>
    </VueFinalModal>

    <div class="create-quote-wrapper">
      <div class="create-quote-main-col" ref="mainCol">
        <!-- Form principale -->
        <form
          class="create-quote-form"
          :class="{ 'form--readonly': readOnly }"
          @submit.prevent="readOnly ? undefined : onUpdateQuoteAndGeneratePdf"
        >
          <!-- SEZIONE: Anagrafica (preventivo o ordine, secondo la rotta) -->
          <section class="quote-section">
            <h3 class="quote-section__title">
              <i class="fa-solid fa-file-lines"></i>
              <!-- [sc-48258] Preventivo o ordine lo dice la rotta: la pagina è
                   la stessa, montata sotto `preventivi/` o sotto `ordini/`. -->
              {{ isOrderRoute ? "Anagrafica ordine" : "Anagrafica preventivo" }}
            </h3>
            <div class="quote-section__grid">
              <!-- Colonna 1: Cliente + Modalità di pagamento -->
              <div class="quote-section__col">
                <!-- Seleziona Cliente -->
                <div class="field" id="client-select">
                  <label>Cliente</label>

                  <SearchableCombo
                    v-model="clientIdModel"
                    v-model:text="searchQueryClient"
                    :items="filteredClients"
                    :labelKey="getClientLabel"
                    :valueKey="(c) => String(c?.id)"
                    placeholder="Cerca cliente…"
                    :loading="false"
                    :disabled="readOnly"
                    @search="
                      (q) => {
                        searchQueryClient = q || '';
                      }
                    "
                    @select="
                      (c) => {
                        formData.clientId = c?.id ?? '';
                      }
                    "
                  />
                </div>

                <!-- Modalità di pagamento -->
                <div class="field">
                  <label for="payment-method">Modalità di pagamento</label>
                  <StatusSelect
                    v-model="formData.payment_method"
                    :options="paymentOptions"
                    placeholder="Seleziona metodo..."
                    placeholderIcon="fa-wallet"
                    :disabled="readOnly"
                  />
                </div>
              </div>

              <!-- Colonna 2: Data + Indirizzo -->
              <div class="quote-section__col">
                <!-- Data Preventivo -->
                <div class="field">
                  <label for="date">Data</label>
                  <FlatPickr
                    id="date"
                    v-model="formData.date"
                    :config="dateConfig"
                    placeholder="gg/mm/aaaa"
                    :disabled="readOnly"
                  />
                </div>

                <!-- Indirizzo: sempre visibile; readonly + nota "Ritiro in sede" quando non è consegna -->
                <div class="field">
                  <label for="address">Indirizzo di consegna</label>
                  <input
                    type="text"
                    id="address"
                    v-model="addressModel"
                    :readonly="readOnly || formData.deliveryType !== 'consegna'"
                    :class="{
                      'readonly-field':
                        readOnly || formData.deliveryType !== 'consegna',
                    }"
                    placeholder="Inserisci indirizzo completo…"
                    autocomplete="off"
                    @mousedown="readOnly ? $event.preventDefault() : undefined"
                  />
                </div>
              </div>

              <!-- Colonna 3: Modalità di consegna -->
              <div class="quote-section__col">
                <div class="field">
                  <label>
                    Modalità di consegna <span class="required-mark">*</span>
                  </label>
                  <StatusSelect
                    v-model="deliveryTypeModel"
                    :options="deliveryOptions"
                    placeholder="Seleziona modalità..."
                    placeholderIcon="fa-truck"
                    :disabled="readOnly"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- RIGA: form articolo (40%) + articoli aggiunti (60%) -->
          <div
            class="quote-work-row"
            :class="{ 'quote-work-row--full': readOnly }"
          >
            <!-- Colonna sinistra (40%): form articolo + note e addebiti -->
            <div class="quote-work-col">
              <!-- SEZIONE: Articolo (form) -->
              <section v-if="!readOnly" class="quote-section">
                <h3 class="quote-section__title">
                  <i class="fa-solid fa-cart-shopping"></i> Articolo
                </h3>
                <div class="quote-section__col">
                  <div class="create-quote-row row-3">
                    <!-- scegli Articolo -->
                    <div class="field" id="item-select">
                      <label>Articolo</label>

                      <SearchableCombo
                        ref="itemCombo"
                        v-model="newItem.itemId"
                        v-model:text="searchQueryItem"
                        :items="filteredItems"
                        :labelKey="itemSearchLabel"
                        :valueKey="(it) => it?.id"
                        placeholder="Cerca articolo…"
                        :disabled="false"
                        :loading="itemsLoading"
                        :current-page="itemsPage"
                        :requested-page="itemsRequestedPage"
                        :total-pages="itemsTotalPages"
                        :total-items="itemsTotalItems"
                        :show-pagination="true"
                        @search="(q) => debouncedFetchItems((q || '').trim())"
                        @select="selectItem"
                        @page-change="onItemPageChange"
                        @cleared="clearItemSelection"
                      >
                        <template #option="{ option }">
                          <span class="quote-item-option">
                            <span class="quote-item-option__content">
                              <span
                                class="quote-item-option__label"
                                :class="{
                                  'is-out': isOrderableOutOfStock(option),
                                }"
                              >
                                <span v-if="isOrderableOutOfStock(option)"
                                  >! </span
                                >{{ itemSearchLabel(option) }}
                              </span>
                              <span
                                v-if="itemFormatFinish(option)"
                                class="quote-item-option__meta"
                              >
                                {{ itemFormatFinish(option) }}
                              </span>
                            </span>
                            <span
                              class="quote-item-option__stock"
                              :class="{
                                'in-stock': warehouseCount(option) > 0,
                              }"
                              :title="stockTitle(option)"
                            >
                              {{ stockLabel(option) }}
                            </span>
                          </span>
                        </template>
                      </SearchableCombo>
                      <p class="create-item-cta">
                        Articolo non trovato?
                        <button
                          type="button"
                          class="create-item-link"
                          @click.prevent.stop="openCreateItemModal"
                        >
                          <i class="fa-solid fa-circle-plus"></i> Crea articolo
                        </button>
                      </p>
                    </div>

                    <!-- Quantità a pezzi: solo per articoli non venduti a scatola -->
                    <div class="field" v-if="!showMqField">
                      <label for="quantity">{{ quantityLabel }}</label>
                      <input
                        id="quantity"
                        step="1"
                        min="1"
                        type="number"
                        v-model="quantityInput"
                        placeholder="Inserisci"
                      />
                    </div>

                    <!-- Articoli a mq/ml: si scrivono i mq e basta. Le scatole intere
                         necessarie sono derivate dietro le quinte e mostrate nell'hint. -->
                    <div class="field" v-else>
                      <label for="mq-per-box">{{ mqFieldLabel }}</label>
                      <input
                        id="mq-per-box"
                        type="number"
                        min="0"
                        step="any"
                        v-model="mqInput"
                        placeholder="Inserisci i mq"
                      />
                      <!-- Dati scatola incompleti: l'articolo si può comunque
                           preventivare, ma la scheda va completata prima di
                           confermare il preventivo al cliente. -->
                      <small
                        v-if="missingBoxDataInfo"
                        class="field-warning"
                        role="alert"
                      >
                        {{ missingBoxDataInfo.text }}
                        <router-link
                          :to="missingBoxDataInfo.route"
                          target="_blank"
                          class="field-warning-link"
                          >Completa la scheda articolo</router-link
                        >
                      </small>
                      <small v-if="mqSummary" class="field-hint">{{
                        mqSummary
                      }}</small>
                    </div>
                  </div>

                  <!-- Flusso prezzo (IVA esclusa): listino → scontato → IVA -->
                  <div
                    v-if="!readOnly"
                    class="create-quote-row row-4 price-flow"
                  >
                    <!-- 1) Prezzo di listino NETTO (IVA esclusa), readonly -->
                    <div class="field">
                      <label for="price">Prezzo listino — IVA esclusa</label>
                      <input
                        id="price"
                        type="number"
                        v-model="newItem.price"
                        placeholder="Seleziona l'articolo per visualizzare il prezzo corrispondente"
                        step="0.01"
                        readonly
                        class="readonly-field"
                      />
                    </div>
                    <i
                      class="fa-solid fa-arrow-right price-flow__arrow"
                      aria-hidden="true"
                    ></i>
                    <!-- 2) Prezzo scontato NETTO (opzionale): prezzo netto da
                 applicare, digitato dall'operatore. Vuoto → usa il listino. -->
                    <div class="field">
                      <label for="discounted-price">Prezzo scontato</label>
                      <input
                        id="discounted-price"
                        type="number"
                        v-model="newItem.discountedPrice"
                        placeholder="Prezzo netto scontato (opzionale)"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <i
                      class="fa-solid fa-arrow-right price-flow__arrow"
                      aria-hidden="true"
                    ></i>
                    <!-- 3) IVA applicata alla riga (default 0%) -->
                    <div class="field">
                      <label for="vat-rate">IVA</label>
                      <StatusSelect
                        v-model="newItem.iva"
                        :options="ivaOptions"
                        placeholderIcon="fa-percent"
                      />
                    </div>
                  </div>

                  <!-- Anteprima riga: imponibile → imposta → totale (IVA esclusa
                       → ivato). Solo preview, non input. -->
                  <div v-if="!readOnly" class="create-quote-row line-preview">
                    <span class="line-preview__item"
                      >Imponibile:
                      <strong>€ {{ previewFormatted.taxable }}</strong></span
                    >
                    <span class="line-preview__item"
                      >Aliquota: <strong>{{ newItem.iva }}%</strong></span
                    >
                    <span class="line-preview__item"
                      >Imposta:
                      <strong>€ {{ previewFormatted.tax }}</strong></span
                    >
                    <span class="line-preview__item line-preview__total"
                      >Totale:
                      <strong>€ {{ previewFormatted.gross }}</strong></span
                    >
                  </div>

                  <!-- RIGA 5: bottone "+ aggiungi articolo" + "Mostra articoli" -->
                  <div v-if="!readOnly" class="create-quote-row row-5">
                    <!-- Bottone per aggiungere un ulteriore alla lista -->
                    <div class="field">
                      <button
                        type="button"
                        class="add-item-btn"
                        :disabled="!canAddItem"
                        @click="addOrUpdateItem"
                      >
                        <!-- icona -->
                        <i class="fa-solid fa-plus icon" aria-hidden="true"></i>
                        <span>aggiungi articolo</span>
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- SEZIONE: Note e addebiti (sotto il form articolo) -->
              <section class="quote-section">
                <h3 class="quote-section__title">
                  <i class="fa-solid fa-note-sticky"></i> Note e addebiti
                </h3>
                <!-- RIGA 6: note -->
                <div class="create-quote-row row-5 notes-row">
                  <div class="field">
                    <label for="technical-requests">Note</label>
                    <textarea
                      id="technical-requests"
                      v-model.trim="formData.technical_queries"
                      placeholder="Inserisci note"
                      rows="3"
                      :readonly="readOnly"
                      @mousedown="
                        readOnly ? $event.preventDefault() : undefined
                      "
                    ></textarea>
                  </div>
                </div>

                <!-- RIGA 7: Addebiti -->
                <div class="create-quote-row addebiti-row">
                  <div class="field">
                    <label>Addebiti</label>
                    <hr />
                    <div class="addebiti-line">
                      <span class="addebiti-label">Spesa imballo (€30,00)</span>
                      <label
                        class="addebiti-option"
                        :style="
                          readOnly
                            ? { cursor: 'default', pointerEvents: 'none' }
                            : {}
                        "
                      >
                        <input
                          type="radio"
                          :value="false"
                          v-model="formData.hasPackagingFee"
                          :disabled="readOnly"
                        />
                        No
                      </label>
                      <label
                        class="addebiti-option"
                        :style="
                          readOnly
                            ? { cursor: 'default', pointerEvents: 'none' }
                            : {}
                        "
                      >
                        <input
                          type="radio"
                          :value="true"
                          v-model="formData.hasPackagingFee"
                          :disabled="readOnly"
                        />
                        Sì
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <!-- /.quote-work-col -->

            <!-- Colonna destra (60%): articoli aggiunti + salva -->
            <div class="quote-work-col">
              <!-- Articoli aggiunti -->
              <aside class="items-panel" ref="itemsPanel">
                <div class="items-panel__title-bar">
                  <h3 class="items-panel__title">Articoli aggiunti</h3>
                  <!-- [sc-48248] Suddivisioni del preventivo ("Bagno 2") -->
                  <button
                    v-if="!readOnly"
                    type="button"
                    class="section-add-btn"
                    @click="apriNuovaSuddivisione"
                    title="Aggiungi una suddivisione al preventivo"
                  >
                    <i class="fa-solid fa-layer-group"></i>
                    Suddivisione
                  </button>
                </div>
                <div
                  v-if="nuovaSuddivisioneAperta && !readOnly"
                  class="section-new-bar"
                >
                  <input
                    v-model="nuovaSuddivisioneNome"
                    type="text"
                    maxlength="100"
                    class="section-new-input"
                    placeholder="Nome della suddivisione — es. Bagno 2"
                    @keyup.enter="confermaNuovaSuddivisione"
                    @keyup.esc="annullaNuovaSuddivisione"
                  />
                  <button
                    type="button"
                    class="primary-btn section-new-confirm"
                    @click="confermaNuovaSuddivisione"
                  >
                    Aggiungi
                  </button>
                  <button
                    type="button"
                    class="secondary-btn"
                    @click="annullaNuovaSuddivisione"
                  >
                    Annulla
                  </button>
                </div>
                <div class="items-panel__table-wrap">
                  <table class="items-panel__table">
                    <thead>
                      <tr>
                        <!-- [sc-48248] colonna della maniglia di trascinamento -->
                        <th class="drag-handle-col"></th>
                        <th>Codice</th>
                        <th>Articolo</th>
                        <th>Categoria</th>
                        <th>Quantità</th>
                        <th>Prezzo</th>
                        <!-- [sc-49019] Totale di riga, IVA esclusa come il prezzo
                             accanto: il conto si rifà a occhio. -->
                        <th
                          class="row-total-head"
                          title="Prezzo × quantità, IVA esclusa"
                        >
                          Totale
                        </th>
                        <th>IVA</th>
                        <th></th>
                      </tr>
                    </thead>
                    <!-- [sc-48248] Un <tbody> per suddivisione: l'intestazione
                         sta in un tbody suo, le righe in uno trascinabile. Più
                         <tbody> nella stessa tabella sono HTML valido, ed è
                         l'unico modo di dare a Sortable un contenitore per
                         lista senza rinunciare alle intestazioni. -->
                    <template
                      v-for="gruppo in gruppiArticoli"
                      :key="`grp-${gruppo.nome || '__nessuna__'}`"
                    >
                      <tbody v-if="gruppo.nome">
                        <tr class="section-header-row">
                          <td colspan="9">
                            <template
                              v-if="
                                rinominaSuddivisione === gruppo.nome &&
                                !readOnly
                              "
                            >
                              <!-- [sc-48248] Rinomina: stessa barra della riga normale, campo
                                   della larghezza del nome e due icone piccole. -->
                              <span class="section-header-bar">
                                <i class="fa-solid fa-layer-group"></i>
                                <input
                                  v-model="rinominaNome"
                                  type="text"
                                  maxlength="100"
                                  class="section-rename-input"
                                  placeholder="Nome della suddivisione"
                                  @keyup.enter="confermaRinomina(gruppo.nome)"
                                  @keyup.esc="rinominaSuddivisione = null"
                                />
                                <button
                                  type="button"
                                  class="section-rename-ok"
                                  @click="confermaRinomina(gruppo.nome)"
                                  title="Conferma il nuovo nome"
                                  aria-label="Conferma il nuovo nome"
                                >
                                  <i class="fa-solid fa-check"></i>
                                </button>
                                <button
                                  type="button"
                                  class="section-rename-undo"
                                  @click="rinominaSuddivisione = null"
                                  title="Annulla"
                                  aria-label="Annulla"
                                >
                                  <i class="fa-solid fa-xmark"></i>
                                </button>
                              </span>
                            </template>
                            <template v-else>
                              <span class="section-header-bar">
                                <i class="fa-solid fa-layer-group"></i>
                                <!-- [sc-48248] Il nome è il comando di rinomina: cliccarlo apre
                                     il campo. Prima c'era un bottone a parte lì accanto. -->
                                <button
                                  type="button"
                                  class="section-name"
                                  :disabled="readOnly"
                                  @click="apriRinomina(gruppo.nome)"
                                  title="Clicca sul nome per rinominare la suddivisione"
                                >
                                  {{ gruppo.nome }}
                                </button>
                                <!-- Rimozione: icona piccola accanto al nome, non un bottone
                                     pieno. Toglie il raggruppamento, non gli articoli. -->
                                <button
                                  type="button"
                                  class="section-remove"
                                  :disabled="readOnly"
                                  @click="rimuoviSuddivisione(gruppo.nome)"
                                  title="Togli la suddivisione (gli articoli restano)"
                                  aria-label="Togli la suddivisione"
                                >
                                  <i class="fa-solid fa-xmark"></i>
                                </button>
                                <span class="section-count">
                                  {{ gruppo.righe.length }}
                                  {{
                                    gruppo.righe.length === 1
                                      ? "articolo"
                                      : "articoli"
                                  }}
                                </span>
                                <span class="section-total"
                                  >Totale
                                  {{
                                    totaleSuddivisioneFormattato(gruppo)
                                  }}</span
                                >
                              </span>
                            </template>
                          </td>
                        </tr>
                      </tbody>
                      <draggable
                        tag="tbody"
                        :list="gruppo.righe"
                        group="righe-preventivo"
                        handle=".drag-handle"
                        item-key="itemId"
                        ghost-class="quote-item-row--ghost"
                        drag-class="quote-item-row--dragging"
                        :animation="150"
                        :disabled="readOnly"
                        @change="applicaSpostamento(gruppo, $event)"
                      >
                        <template #item="{ element: item }">
                          <tr
                            :class="{
                              'quote-item-row--incomplete':
                                isRowStockIncomplete(item),
                            }"
                            :title="rowStockWarning(item)"
                          >
                            <td class="drag-handle-cell">
                              <i
                                v-if="!readOnly"
                                class="fa-solid fa-grip-vertical drag-handle"
                                title="Trascina per spostare l'articolo"
                              ></i>
                            </td>
                            <td class="item-code-cell">
                              {{
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                ).supplierCode ||
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                ).itemCode ||
                                "—"
                              }}
                            </td>
                            <td>
                              <div class="item-title">
                                {{
                                  displayForId(
                                    item.itemId,
                                    categoryHintForRow(item)
                                  ).title
                                }}
                              </div>
                              <div
                                v-if="
                                  displayForId(
                                    item.itemId,
                                    categoryHintForRow(item)
                                  ).meta
                                "
                                class="item-meta"
                              >
                                {{
                                  displayForId(
                                    item.itemId,
                                    categoryHintForRow(item)
                                  ).meta
                                }}
                              </div>
                              <!-- [sc-48248] Compare solo quando una suddivisione esiste:
                                   senza, sarebbe una tendina a una voce sola. -->
                              <!-- [sc-48248] Compare solo quando una suddivisione esiste:
                                   senza, sarebbe una tendina a una voce sola. Stessa veste
                                   degli altri campi del form. -->
                              <StatusSelect
                                v-if="nomiSuddivisioni.length"
                                class="section-move-select"
                                :model-value="item.sectionName || ''"
                                :options="opzioniSuddivisione"
                                :disabled="readOnly"
                                placeholderIcon="fa-layer-group"
                                @update:modelValue="
                                  spostaInSuddivisione(item, $event)
                                "
                              />
                              <!-- [sc-48248] Nota libera: dentro la cella
                                   dell'articolo, così ogni articolo resta una
                                   riga sola e Sortable sa cosa sta spostando. -->
                              <input
                                v-if="notaAperta(item)"
                                type="text"
                                class="item-note-input"
                                maxlength="500"
                                :value="item.note || ''"
                                :disabled="readOnly"
                                placeholder="Nota per il cliente — Invio per chiudere"
                                title="La nota si salva mentre scrivi: finisce nel preventivo al prossimo salvataggio"
                                @input="
                                  readOnly
                                    ? undefined
                                    : scriviNota(item, $event.target.value)
                                "
                                @change="
                                  readOnly
                                    ? undefined
                                    : updateRowNote(item, $event.target.value)
                                "
                                @keyup.enter="$event.target.blur()"
                              />
                            </td>
                            <td>
                              {{
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                ).category
                              }}
                            </td>
                            <td>
                              <input
                                type="number"
                                class="panel-qty-input"
                                :value="rowQtyValue(item)"
                                :step="rowQtyStep(item)"
                                :title="rowQtyTitle(item)"
                                min="0"
                                :disabled="readOnly"
                                @change="
                                  readOnly
                                    ? undefined
                                    : updateRowQty(
                                        item,
                                        +$event.target.value,
                                        $event.target
                                      )
                                "
                              />
                              <span class="panel-qty-unit">{{
                                rowQtyUnit(item)
                              }}</span>
                              <!-- Disponibilità mostrata solo se le scatole sono
                               calcolabili: senza mq/ml per scatola i numeri
                               sarebbero inventati. -->
                              <template
                                v-if="
                                  rowHasBoxData(item) &&
                                  isOrderableOutOfStock(
                                    getItemFromStore(
                                      item.itemId,
                                      categoryHintForRow(item)
                                    )
                                  )
                                "
                              >
                                <span class="item-order-queued-badge"
                                  >Esaurito</span
                                >
                                <span class="item-order-info-msg"
                                  >Necessario ordinarlo per la quantità
                                  richiesta.</span
                                >
                              </template>
                              <template
                                v-else-if="
                                  rowHasBoxData(item) &&
                                  isQtyExceedingStock(item)
                                "
                              >
                                <span
                                  class="item-order-queued-badge item-not-available-badge"
                                  >Non disponibile</span
                                >
                                <span class="item-order-info-msg"
                                  >Necessario ordinarne
                                  {{ qtyToOrderLabel(item) }} per la quantità
                                  richiesta.</span
                                >
                              </template>
                            </td>
                            <!-- [sc-48248] Prezzo e IVA si correggono qui, senza
                             riaprire la scheda dell'articolo. Su un preventivo
                             bloccato restano in sola lettura come il resto. -->
                            <td>
                              <input
                                type="number"
                                class="panel-price-input"
                                :value="rowPriceValue(item)"
                                min="0"
                                step="0.01"
                                title="Prezzo netto applicato alla riga"
                                :disabled="readOnly"
                                @change="
                                  readOnly
                                    ? undefined
                                    : updateRowPrice(
                                        item,
                                        $event.target.value,
                                        $event.target
                                      )
                                "
                              />
                              <span class="panel-qty-unit">{{
                                unitForRow(item)
                              }}</span>
                            </td>
                            <td
                              class="row-total-cell"
                              title="Prezzo × quantità, IVA esclusa"
                            >
                              {{
                                totaleRigaImponibileFormattato(
                                  item.price,
                                  rowQtyValue(item)
                                )
                              }}
                            </td>
                            <td>
                              <!-- [sc-48248] Stessa tendina degli altri campi del form, non
                                   quella nativa del browser. -->
                              <StatusSelect
                                class="panel-iva-select"
                                :model-value="item.iva ?? 22"
                                :options="ivaOptions"
                                :disabled="readOnly"
                                placeholderIcon="fa-percent"
                                @update:modelValue="updateRowIva(item, $event)"
                              />
                            </td>
                            <td>
                              <button
                                v-if="!readOnly"
                                type="button"
                                class="icons-button"
                                id="remove-item-button"
                                @click="
                                  removeItem(formData.items.indexOf(item))
                                "
                                title="Rimuovi articolo dalla lista"
                                aria-label="Rimuovi articolo dalla lista"
                              >
                                <i class="fa-solid fa-trash"></i>
                              </button>
                              <!-- [sc-48248] Nota libera: su un preventivo in sola
                               lettura la si legge soltanto, e il bottone per
                               aggiungerne una non serve. -->
                              <button
                                v-if="!readOnly"
                                type="button"
                                class="icons-button icons-button--plain"
                                :class="{ 'icons-button--on': !!item.note }"
                                @click="toggleNota(item)"
                                :title="
                                  item.note
                                    ? 'Modifica la nota di questo articolo'
                                    : 'Aggiungi una nota a questo articolo'
                                "
                                :aria-label="
                                  item.note ? 'Modifica nota' : 'Aggiungi nota'
                                "
                              >
                                <i class="fa-regular fa-comment-dots"></i>
                              </button>
                            </td>
                          </tr>
                        </template>
                      </draggable>
                    </template>
                    <tbody>
                      <tr v-if="formData.items.length === 0">
                        <td colspan="9" class="no-result">
                          Nessun articolo presente
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="items-panel__footer">
                  {{ formData.items.length }}
                  {{ formData.items.length === 1 ? "articolo" : "articoli" }}
                </div>
              </aside>

              <!-- Salva/Aggiorna: sotto il riepilogo articoli aggiunti -->
              <div class="save-button-container">
                <template v-if="readOnly">
                  <!-- [sc-48735] L'avviso prende il posto di «Modifica». -->
                  <p v-if="isAdmin && modificaChiusa" class="modifica-chiusa">
                    {{ testoModificaChiusa }}
                  </p>
                  <router-link
                    v-else-if="isAdmin"
                    :to="
                      isOrderRoute
                        ? `/home/ordini/modifica/${quoteId}`
                        : `/home/preventivi/modifica/${quoteId}`
                    "
                    class="primary-btn"
                    :title="
                      isOrderRoute ? 'Modifica ordine' : 'Modifica preventivo'
                    "
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                    Modifica
                  </router-link>
                </template>
                <template v-else>
                  <!-- [sc-48735] Arrivato dall'indirizzo diretto di modifica. -->
                  <p v-if="modificaChiusa" class="modifica-chiusa">
                    {{ testoModificaChiusa }}
                  </p>
                  <button
                    type="button"
                    id="save-quote"
                    class="primary-btn"
                    @click="onUpdateQuoteAndGeneratePdf"
                    :disabled="!canSave"
                    :aria-disabled="!canSave ? 'true' : 'false'"
                    :title="
                      modificaChiusa
                        ? 'Pagato e in consegna: non modificabile'
                        : !canSave
                        ? isOrderRoute
                          ? 'Modifica almeno uno dei campi per aggiornare l\'ordine'
                          : 'Modifica almeno uno dei campi per aggiornare il preventivo'
                        : isOrderRoute
                        ? 'Aggiorna ordine ed esporta PDF'
                        : 'Aggiorna preventivo ed esporta PDF'
                    "
                  >
                    Aggiorna ed esporta PDF
                  </button>

                  <button
                    type="button"
                    id="send-email-quote"
                    @click="askSendDocumentEmail"
                    :disabled="!pdfUploaded || sendingEmail"
                    :title="
                      !pdfUploaded
                        ? 'Genera prima il PDF per poter inviare l\'email'
                        : 'Invia documento via email/PEC al cliente'
                    "
                  >
                    <i class="fa-solid fa-envelope"></i>
                    {{
                      sendingEmail
                        ? "Invio in corso..."
                        : "Invia email al cliente"
                    }}
                  </button>

                  <router-link
                    :to="
                      isOrderRoute
                        ? `/home/ordini/dettaglio/${quoteId}`
                        : `/home/preventivi/dettaglio/${quoteId}`
                    "
                    class="secondary-btn"
                    :title="
                      isOrderRoute
                        ? 'Torna alla visualizzazione ordine'
                        : 'Torna alla visualizzazione preventivo'
                    "
                  >
                    <i class="fa-solid fa-eye"></i>
                    Visualizza
                  </router-link>
                </template>
              </div>
            </div>
            <!-- /.quote-work-col -->
          </div>
          <!-- /.quote-work-row -->
        </form>
      </div>
      <!-- /.create-quote-main-col -->
    </div>
    <!-- /.create-quote-wrapper -->
    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">Le modifiche non salvate andranno perse.</p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            Conferma
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modale "Crea articolo": crea l'articolo e lo auto-seleziona nel preventivo -->
    <CreateItemModal
      v-model="showCreateItemModal"
      :initial-name="searchQueryItem"
      @created="onItemCreated"
    />
  </main>
</template>

<script>
import logoPath from "@/assets/logo-houselet.png";
// import crypto from "crypto";
import axiosInstance from "@/axiosInstance";
import { generateQuotePdf } from "@/utilities/generateQuotePdf";
import { ordineNonModificabile } from "@/utilities/pagamenti";
import {
  missingStockFields,
  hasIncompleteStockData,
  itemPerBox,
} from "@/utilities/itemStockData";
import debounce from "lodash/debounce";
// import { nextTick } from "process";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import {
  formatToLocalDate,
  toDateLocal,
  formatDecimal,
} from "../utilities/formatting";
import { totaleRigaImponibileFormattato } from "../utilities/rigaPreventivo";
import SearchableCombo from "./SearchableCombo.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import CreateItemModal from "@/components/CreateItemModal.vue";
import FlatPickr from "vue-flatpickr-component";
import draggable from "vuedraggable";
import { Italian } from "flatpickr/dist/l10n/it.js";
import apriDocumento from "@/mixins/apriDocumento";

// Chiavi cache namespaced
const asKey = (id) => String(id ?? ""); // normalizza sempre a stringa
const KEY = {
  STOCK: (id) => `STOCK:${asKey(id)}`,
};

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

/* Categorie vendute a scatola/confezione (la quantità va in boxQuantity,
   non in pieces): tutto il gruppo area + linear, non solo le piastrelle. */
function isBoxBasedCategory(category) {
  const g = getCategoryGroup(category);
  return g === "area" || g === "linear";
}

/* Quantità in mq/ml corrispondente a N scatole, come multiplo esatto di
   perBox (evita 3 x 1,44 = 4,319999...). Stringa pronta per il campo input:
   punto decimale, zeri finali rimossi. */
function quantityFromBoxes(boxes, perBox) {
  const n = Number(boxes) || 0;
  const scaled = Math.round((Number(perBox) || 0) * 10000);
  if (!n || !scaled) return "";
  return ((n * scaled) / 10000)
    .toFixed(4)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
}

/* Scatole intere necessarie per una quantità in mq/ml.
   Arrotonda per eccesso (si comprano scatole intere) ma con tolleranza: senza,
   l'errore float o l'arrotondamento del campo fa scattare una scatola in più
   (es. 4,32 / 1,44 = 3.0000000000000004 -> ceil = 4 scatole invece di 3). */
function boxesFromQuantity(quantity, perBox) {
  const qty = Number(quantity) || 0;
  const step = Number(perBox) || 0;
  if (!step || qty <= 0) return 0;
  const ratio = qty / step;
  const nearest = Math.round(ratio);
  const tolerance = Math.min(0.005 / step, 0.01);
  return Math.abs(ratio - nearest) <= tolerance ? nearest : Math.ceil(ratio);
}

// mq/ml persistiti dall'API: null sulle righe storiche e sugli articoli a pezzo
function mqFromApi(value) {
  const n = Number(value);
  return n > 0 ? n : null;
}

// mq/ml per scatola con fallback: se l'articolo non ha il dato, si assume
// 1 unità per scatola — stessa assunzione dell'API nel calcolo del totale
// (COALESCE(sqmPerBox, 1)) — così l'articolo resta comunque preventivabile.
function perBoxOrFallback(perBox) {
  const n = Number(perBox) || 0;
  return n > 0 ? n : 1;
}

export default {
  name: "EditQuotePage",
  mixins: [apriDocumento],
  components: {
    SearchableCombo,
    StatusSelect,
    CreateItemModal,
    FlatPickr,
    draggable,
  },
  beforeRouteLeave(to) {
    if (
      this.readOnly ||
      this.navigationConfirmed ||
      to.name === "login" ||
      !this.isDirty
    ) {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  props: {
    quoteId: [String, Number],
    readOnly: { type: Boolean, default: false },
  },
  created() {
    this.$store
      .dispatch("fetchClients", {
        limit: 10000,
      })
      .catch((err) => {
        // evita Uncaught (in promise)
        console.error("Impossibile caricare i clienti:", err.message);
        // puoi mostrare un toast o impostare un flag di errore
      });
    // this.$store.dispatch("fetchOperators");
    this.$store.dispatch("fetchItems", { limit: 10000 }).catch((err) => {
      // evita Uncaught (in promise)
      console.error("Impossibile caricare gli articoli:", err.message);
      // puoi mostrare un toast o impostare un flag di errore
    });
    this.debouncedFetchItems = debounce(this.fetchItems, 200);
  },
  computed: {
    /* [sc-48248] Nomi delle suddivisioni, nell'ordine in cui compaiono fra gli
       articoli; in coda quelle appena create, che non hanno ancora righe. */
    nomiSuddivisioni() {
      const nomi = [];
      for (const riga of this.formData.items || []) {
        const n = (riga.sectionName || "").trim();
        if (n && !nomi.includes(n)) nomi.push(n);
      }
      for (const n of this.suddivisioniVuote) {
        if (!nomi.includes(n)) nomi.push(n);
      }
      return nomi;
    },

    /* [sc-48248] Voci della tendina di spostamento: la prima toglie la riga
       da ogni suddivisione. */
    opzioniSuddivisione() {
      return [
        { value: "", label: "Senza suddivisione" },
        ...this.nomiSuddivisioni.map((nome) => ({ value: nome, label: nome })),
      ];
    },

    /* [sc-48248] Le righe raggruppate per la tabella. Gli articoli senza
       suddivisione stanno in cima: è come si sono sempre visti i preventivi, e
       un preventivo che non usa le suddivisioni non deve cambiare aspetto. */
    gruppiArticoli() {
      const righe = this.formData.items || [];
      const senza = righe.filter((r) => !(r.sectionName || "").trim());
      const gruppi = senza.length ? [{ nome: null, righe: senza }] : [];

      for (const nome of this.nomiSuddivisioni) {
        gruppi.push({
          nome,
          righe: righe.filter((r) => (r.sectionName || "").trim() === nome),
        });
      }
      return gruppi;
    },

    // Indirizzo mostrato nel campo: "Ritiro in sede" quando non è consegna
    // (solo visuale; il valore reale formData.location.address resta invariato)
    addressModel: {
      get() {
        return this.formData.deliveryType === "ritiro"
          ? "Ritiro in sede"
          : this.formData.location.address;
      },
      set(v) {
        if (this.formData.deliveryType === "consegna") {
          this.formData.location.address = v;
        }
      },
    },
    // Ponte UI per la select "Modalità di consegna": il payload resta
    // "consegna" | "ritiro" | "". Stessa logica di azzeramento indirizzo.
    deliveryTypeModel: {
      get() {
        return this.formData.deliveryType;
      },
      set(v) {
        this.formData.deliveryType = v || "";
        if (this.formData.deliveryType !== "consegna") {
          this.formData.location.address = "";
        }
      },
    },
    canSave() {
      // articoli obbligatori + modalità (consegna/ritiro) obbligatoria
      const hasItems = (this.formData.items || []).length > 0;
      return (
        hasItems &&
        !!this.formData.deliveryType &&
        this.isDirty &&
        !this.dupLoading &&
        !this.modificaChiusa
      );
    },
    // true solo se lo stato attuale differisce dallo snapshot iniziale.
    // Finché lo snapshot non è acquisito (originalSig vuoto) → non "sporco".
    isDirty() {
      if (!this.originalSig) return false;
      return this.computeSignature() !== this.originalSig;
    },
    isAdmin() {
      return this.$store.state.currentUser?.isAdmin ?? false;
    },
    testoModificaChiusa() {
      return this.isOrderRoute
        ? "Ordine pagato e con la consegna iniziata: non si modifica più."
        : "Preventivo pagato e con la consegna iniziata: non si modifica più.";
    },
    isOrderRoute() {
      return ["edit-order", "view-order"].includes(this.$route.name);
    },
    isAddressValid() {
      return !!(this.formData.location?.address || "").trim();
    },

    // selectedItem() {
    //   /* Se è stato scelto un articolo di magazzino */
    //   if (this.newItem.itemId) {
    //     return this.getItemFromStore(this.newItem.itemId); // ← già fa cache
    //   }
    //   /* Se è stato scelto un articolo FUORI magazzino */
    //   if (this.newItem.outstockId) {
    //     return (
    //       this.outstocks.find((o) => o.id === this.newItem.outstockId) || {}
    //     );
    //   }
    //   /* Nessuna selezione */
    //   return {};
    // },

    selectedItem() {
      if (this.newItem.itemId) {
        return this.getItemFromStore(this.newItem.itemId, "STOCK");
      }
      return {};
    },

    selectedItemGroup() {
      return getCategoryGroup(this.selectedItem?.category);
    },
    mqFieldLabel() {
      if (this.selectedItemGroup === "linear") return "Quantità ML";
      return "Quantità Mq";
    },
    quantityLabel() {
      if (this.selectedItemGroup === "area") return "Quantità scatole";
      if (this.selectedItemGroup === "linear") return "Quantità confezioni";
      if (
        this.selectedItem.category === "Fuori Magazzino" &&
        this.selectedItem.subCategory === "piastrelle"
      )
        return "Quantità (mq)";
      if (this.selectedItem.category === "Fuori Magazzino") return "Quantità";
      return "Quantità pezzi";
    },

    // quantityInput: {
    //   get() {
    //     return this.selectedItem.category === "piastrelle"
    //       ? this.newItem.boxQuantity // lavoro in scatole
    //       : this.newItem.pieces; // lavoro in pezzi
    //   },
    //   set(v) {
    //     const n = +v || 0;
    //     if (this.selectedItem.category === "piastrelle") {
    //       // console.log("selectedItem in quantityinput", this.selectedItem);
    //       this.newItem.boxQuantity = n; // scatole
    //       const ppb = this.selectedItem.piecesPerBox || 1;
    //       this.newItem.pieces = n * ppb; // pezzi calcolati
    //       // this.newItem.unitPrice;
    //     } else {
    //       this.newItem.pieces = n; // sanitari
    //     }

    //     // ricalcolo per FUORI MAGAZZINO: totale = unit × qty
    //     if (this.selectedItem.category === "Fuori Magazzino") {
    //       const isTiles = this.selectedItem.subCategory === "piastrelle";
    //       const base = isTiles
    //         ? Number(this.selectedItem.sqmPrice) || 0
    //         : Number(this.selectedItem.price) || 0;

    //       const unit = Number(this.adjustedPrice(base, this.newItem.iva));
    //       this.newItem.unitPrice = unit;

    //       const qty = this.newItem.pieces || 0; // anche per mq
    //       this.newItem.price = (unit * qty).toFixed(2); // mostra il totale nel campo
    //     }
    //   },
    // },

    quantityInput: {
      get() {
        const g = getCategoryGroup(this.selectedItem?.category);
        if (g === "area" || g === "linear") return this.newItem.boxQuantity;
        return this.newItem.pieces;
      },
      set(v) {
        // scatole/confezioni/pezzi sono sempre interi: niente 2,5 scatole
        const n = Math.max(0, Math.round(+v || 0));
        const g = getCategoryGroup(this.selectedItem?.category);

        if (g === "area") {
          this.newItem.boxQuantity = n;
          const ppb = this.selectedItem.piecesPerBox || 1;
          this.newItem.pieces = n * ppb;
        } else if (g === "linear") {
          this.newItem.boxQuantity = n;
          this.newItem.pieces = 0;
        } else {
          this.newItem.pieces = n;
        }

        if (this.selectedItem.category === "Fuori Magazzino") {
          const isTiles = this.selectedItem.subCategory === "piastrelle";
          const base = isTiles
            ? Number(this.selectedItem.sqmPrice) || 0
            : Number(this.selectedItem.price ?? this.newItem.unitPrice) || 0;

          const unit = Number(base) || 0; // prezzo unitario NETTO (IVA esclusa)
          this.newItem.unitPrice = unit;
          // this.newItem.pieces = n;

          const qty = this.newItem.pieces || 0;
          this.newItem.price = (unit * qty).toFixed(2);
        }
      },
    },

    /* Abilita "aggiungi articolo" solo con quantità numerica > 0 */
    canAddItem() {
      const hasSelectedItem = Boolean(this.newItem.itemId);
      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";
      const qty = isAreaOrLinear
        ? Number(this.newItem.boxQuantity)
        : Number(this.newItem.pieces);
      return hasSelectedItem && Number.isFinite(qty) && qty >= 1;
    },

    /* Articolo a mq/ml con la scheda incompleta: si può comunque preventivare,
       ma senza mq/scatola e pz/scatola il preventivo non è confermabile.
       Restituisce l'avviso e la rotta per completare la scheda. */
    missingBoxDataInfo() {
      const item = this.selectedItem;
      if (!this.showMqField || !item?.id) return null;
      const missing = missingStockFields(item);
      if (!missing.length) return null;
      return {
        text: `Scheda articolo incompleta (manca ${missing.join(
          ", "
        )}): puoi aggiungere l'articolo al preventivo, ma completa la scheda prima di confermarlo al cliente.`,
        route: {
          name: "listini-edit-item",
          params: { category: item.category, itemId: item.id },
        },
      };
    },

    /* mq/ml per scatola dell'articolo selezionato: passo di incremento del
       campo mq (una freccia = una scatola) */
    mqPerBox() {
      const s = this.selectedItem || {};
      const g = getCategoryGroup(s.category);
      const perBox =
        g === "linear" ? Number(s.mlPerBox) || 0 : Number(s.sqmPerBox) || 0;
      return perBox > 0 ? perBox : 0;
    },

    /* Riepilogo sotto il campo mq: l'operatore scrive i mq, ma il preventivo va
       a scatole intere. L'hint rende esplicito cosa viene effettivamente venduto. */
    mqSummary() {
      if (!this.showMqField) return "";
      const perBox = this.mqPerBox;
      // dato mancante: al posto del riepilogo si mostra l'avviso con il link
      if (!perBox) return "";
      const boxes = Number(this.newItem.boxQuantity) || 0;
      const isLinear =
        getCategoryGroup(this.selectedItem?.category) === "linear";
      const unit = isLinear ? "ml" : "mq";
      if (!boxes) return `${formatDecimal(perBox)} ${unit}/scatola`;
      const label = isLinear ? "confezioni" : "scatole";
      const total = formatDecimal(quantityFromBoxes(boxes, perBox));
      const ppb = Number(this.selectedItem?.piecesPerBox) || 0;
      const pieces = !isLinear && ppb ? ` · ${boxes * ppb} pz totali` : "";
      return `${boxes} ${label} = ${total} ${unit}${pieces}`;
    },

    /* Anteprima riga (nuova semantica IVA esclusa): netto applicato, quantità
       effettiva, e importi imponibile/imposta/totale dell'intera riga. Valori 0
       se la quantità non è ancora valida (nessun NaN). */
    previewAppliedNet() {
      const personalized = this.optionalNonNegativeNumber(
        this.newItem.discountedPrice
      );
      return personalized !== null
        ? personalized
        : Number(this.newItem.unitPrice ?? this.newItem.price) || 0;
    },
    previewEffectiveQty() {
      const group = this.selectedItemGroup;
      if (group === "area" || group === "linear") {
        const typed = this.typedMqQuantity();
        if (typed !== null) return Number(typed) || 0;
        return quantityFromBoxes(
          Number(this.newItem.boxQuantity) || 0,
          perBoxOrFallback(this.mqPerBox)
        );
      }
      return Number(this.newItem.pieces) || 0;
    },
    linePreview() {
      const net = Number(this.previewAppliedNet) || 0;
      const qty = Number(this.previewEffectiveQty) || 0;
      const iva = Number(this.newItem.iva) || 0;
      const taxable = +(net * qty).toFixed(2);
      const tax = +((taxable * iva) / 100).toFixed(2);
      const gross = +(taxable + tax).toFixed(2);
      return { taxable, tax, gross };
    },
    previewFormatted() {
      const p = this.linePreview;
      return {
        taxable: formatDecimal(p.taxable),
        tax: formatDecimal(p.tax),
        gross: formatDecimal(p.gross),
      };
    },

    showMqField() {
      const s = this.selectedItem || {};
      if (!s || !s.category) return false;
      const g = getCategoryGroup(s.category);
      return g === "area" || g === "linear";
    },

    // [sc-48246] Destinatario dell'invio. Il backend usa email o PEC, nello
    // stesso ordine di precedenza di getClientLabel(): il modale deve mostrare
    // l'indirizzo che verrà usato davvero, non quello che ci si aspetta.
    emailRecipient() {
      const c = (this.clients || []).find(
        (x) => x.id === this.formData.clientId
      );
      return c?.email || c?.pec || null;
    },
    emailConfirmMessage() {
      const c = (this.clients || []).find(
        (x) => x.id === this.formData.clientId
      );
      const nome = c?.clientName ?? "cliente";
      const doc = this.isOrderRoute ? "dell'ordine" : "del preventivo";
      if (!this.emailRecipient) {
        return (
          `<strong>${nome}</strong> non ha né email né PEC in anagrafica: ` +
          `l'invio fallirebbe.`
        );
      }
      return (
        `Inviare il PDF ${doc} <strong>#${this.quoteId}</strong> a ` +
        `<strong>${nome}</strong>, all'indirizzo ` +
        `<strong>${this.emailRecipient}</strong>?`
      );
    },

    clients() {
      return this.$store.getters["clients/list"] ?? [];
    },
    items() {
      return this.$store.getters["items/list"] ?? [];
    },

    filteredClients() {
      const q = (this.searchQueryClient || "").toLowerCase().trim();
      const list = this.clients || [];
      let res = q
        ? list
            .filter((c) => (c.clientName || "").toLowerCase().includes(q))
            .slice(0, 8)
        : list.slice(0, 8);

      const selId = this.formData.clientId;
      const selected = list.find((c) => c.id === selId);
      if (selected && !res.some((c) => c.id === selId)) {
        res = [selected, ...res].slice(0, 8);
      }
      return res;
    },

    // Tutti i nomi da mostrare nella colonna "Articolo"
    itemNames() {
      return (this.formData.items || []).map(
        (row) => this.getItemName(row.itemId, row.category) || ""
      );
    },

    // Lunghezza massima in caratteri tra i nomi
    maxItemLen() {
      return this.itemNames.reduce((m, s) => Math.max(m, s.length), 0);
    },

    // Larghezza dinamica della colonna "Articolo" in ch, con limiti per non rompere il layout
    // - min 180px (leggibile anche con nomi brevi)
    // - valore centrale in ch (caratteri) = maxArtLen + 6 (un po' di respiro)
    // - max 52vw (mai oltre metà viewport nel modale)
    itemColWidth() {
      const ch = Math.max(10, Math.min(this.maxItemLen + 6, 72)); // clamp "logico"
      return `clamp(180px, ${ch}ch, 52vw)`;
    },
    showSelectedItemOrderAlert() {
      return this.isOrderableOutOfStock(this.selectedItem);
    },
    selectedItemOrderLabel() {
      return this.itemSearchLabel(this.selectedItem);
    },

    isStockLocked() {
      return !!this.newItem.itemId;
    },
    clientIdModel: {
      get() {
        const v = this.formData.clientId;
        return v === null || v === undefined ? "" : String(v);
      },
      set(v) {
        // se preferisci tenerlo number nello stato:
        this.formData.clientId = v ? Number(v) : "";
      },
    },
  },

  data() {
    return {
      // [sc-48735] Pagato e con la consegna iniziata: l'API rifiuta la
      // modifica, la pagina lo dice prima.
      modificaChiusa: false,
      dateConfig: {
        locale: Italian,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: true,
        monthSelectorType: "static",
      },
      showBackModal: false,
      pendingNavigation: null,
      navigationConfirmed: false,

      originalSig: "", // firma dello stato iniziale del preventivo, per rilevare modifiche

      showDuplicateModal: false,
      lastDuplicateQuoteInfo: { quoteId: null, address: null },
      showDupDocs: false,
      dupDocuments: [],
      // Modale "Crea articolo" (richiamo da campo ricerca articolo)
      showCreateItemModal: false,
      dupLoading: false,
      dupError: "",
      dupQuote: null,

      showDropdown: false,
      searchQueryItem: "",
      searchQueryClient: "",
      // mq/ml digitati dall'operatore: valore libero, non riscritto dalla UI.
      // Le scatole del preventivo sono derivate da qui (vedi watcher mqInput).
      mqInput: "",
      filteredItems: [],
      // Ricerca articolo: loading + paginazione risultati (fix #2)
      itemsLoading: false,
      itemsPage: 1, // ultima pagina caricata con successo
      itemsRequestedPage: null, // pagina della richiesta in volo (pending)
      itemsTotalPages: 1,
      itemsTotalItems: null,
      itemsSearchQuery: "",
      itemsRequestId: 0,
      pendingOrderItems: {},
      orderNote: "",
      // selectedItemById: {},
      selectedByKey: {},
      // selectedOutstockById: {},
      panelVisible: true,
      activeSearch: null, // 'client' o 'item' o null
      selectedIndex: -1, // Indice dell'opzione attualmente evidenziata
      response: "",
      image: logoPath,

      formData: {
        location: {
          name: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
        },
        clientId: "",
        items: [],
        date: "",
        payment_method: "",
        technical_queries: "",
        deliveryType: "",
        hasPackagingFee: false,
      },
      newItem: {
        itemId: null,
        boxQuantity: 0, // ← scatole, solo per piastrelle
        pieces: 0, // ← pezzi fisici (sanitari) o calcolati
        price: "",
        discountedPrice: "",
        iva: 0,
        category: "",
        // sqmPerBox: "", // solo per piastrelle
      },
      errorMessage: "",
      successMessage: "",
      selectedPlaceId: "",
      paymentOptions: [
        { value: "Contanti", label: "Contanti" },
        { value: "Carta di credito", label: "Carta di credito" },
        { value: "Bonifico", label: "Bonifico" },
        { value: "Scegli successivamente", label: "Scegli successivamente" },
      ],
      deliveryOptions: [
        { value: "consegna", label: "Presso cliente", icon: "fa-truck" },
        { value: "ritiro", label: "Ritiro", icon: "fa-store" },
      ],
      // [sc-48248] Righe con la nota aperta a mano, per itemId.
      noteAperte: {},
      // [sc-48248] Suddivisioni create ma ancora senza articoli dentro. Il
      // nome sta sulla riga, quindi una suddivisione vuota non esiste a DB:
      // vive qui finché non ci si sposta dentro il primo articolo.
      suddivisioniVuote: [],
      nuovaSuddivisioneAperta: false,
      nuovaSuddivisioneNome: "",
      rinominaSuddivisione: null,
      rinominaNome: "",
      ivaOptions: [
        { value: 22, label: "22%" },
        { value: 10, label: "10%" },
        { value: 4, label: "4%" },
        { value: 0, label: "0%" },
      ],
      pdfUploaded: false,
      lastUploadedDocName: "",
      sendingEmail: false,
      // [sc-48246] Conferma invio email
      showEmailModal: false,
    };
  },
  methods: {
    /* [sc-48248] Suddivisioni del preventivo ("Bagno 2"). Il nome sta sulla
       riga: raggruppare per nome significa che non esistono sezioni orfane di
       articoli né articoli orfani di sezione. */
    apriNuovaSuddivisione() {
      this.nuovaSuddivisioneAperta = true;
      this.nuovaSuddivisioneNome = "";
    },

    annullaNuovaSuddivisione() {
      this.nuovaSuddivisioneAperta = false;
      this.nuovaSuddivisioneNome = "";
    },

    confermaNuovaSuddivisione() {
      const nome = (this.nuovaSuddivisioneNome || "").trim();
      if (!nome) {
        this.$toast.error("Dai un nome alla suddivisione.");
        return;
      }
      if (this.nomiSuddivisioni.includes(nome)) {
        this.$toast.error(`La suddivisione «${nome}» esiste già.`);
        return;
      }
      this.suddivisioniVuote = [...this.suddivisioniVuote, nome];
      this.annullaNuovaSuddivisione();
      this.$toast.success(
        `Suddivisione «${nome}» aggiunta: spostaci dentro gli articoli.`
      );
    },

    apriRinomina(nome) {
      this.rinominaSuddivisione = nome;
      this.rinominaNome = nome;
    },

    confermaRinomina(vecchio) {
      const nuovo = (this.rinominaNome || "").trim();
      if (!nuovo) {
        this.$toast.error("Il nome non può restare vuoto.");
        return;
      }
      if (nuovo !== vecchio && this.nomiSuddivisioni.includes(nuovo)) {
        this.$toast.error(`La suddivisione «${nuovo}» esiste già.`);
        return;
      }
      for (const riga of this.formData.items || []) {
        if ((riga.sectionName || "").trim() === vecchio)
          riga.sectionName = nuovo;
      }
      this.suddivisioniVuote = this.suddivisioniVuote.map((n) =>
        n === vecchio ? nuovo : n
      );
      this.rinominaSuddivisione = null;
      this.riallineaPosizioni();
    },

    /* Toglie la suddivisione, non gli articoli: tornano fuori da ogni
       raggruppamento, dove stavano prima. */
    rimuoviSuddivisione(nome) {
      for (const riga of this.formData.items || []) {
        if ((riga.sectionName || "").trim() === nome) riga.sectionName = null;
      }
      this.suddivisioniVuote = this.suddivisioniVuote.filter((n) => n !== nome);
      this.riallineaPosizioni();
    },

    spostaInSuddivisione(riga, nome) {
      const n = (nome || "").trim();
      riga.sectionName = n || null;
      // Appena una suddivisione ha un articolo dentro non è più "vuota": da
      // quel momento esiste perché lo dice la riga.
      if (n)
        this.suddivisioniVuote = this.suddivisioniVuote.filter((x) => x !== n);
      this.riallineaPosizioni();
    },

    /* [sc-48248] Spostamento di una riga, da Sortable.
       `gruppo.righe` è una lista derivata: Sortable la riordina, ma la verità
       sta in `formData.items`. Qui si legge l'ordine appena prodotto e lo si
       riscrive nell'elenco vero, una volta sola. */
    applicaSpostamento(gruppo, evento) {
      // I gruppi si leggono PRIMA di toccare `sectionName`: scrivere quel campo
      // invalida il computed e si perderebbe l'ordine appena prodotto.
      const gruppi = this.gruppiArticoli;
      const nuovoOrdine = gruppi.flatMap((g) => g.righe);

      const arrivata = evento?.added?.element;
      if (arrivata) arrivata.sectionName = gruppo.nome || null;
      if (gruppo.nome) {
        this.suddivisioniVuote = this.suddivisioniVuote.filter(
          (n) => n !== gruppo.nome
        );
      }

      this.formData.items.splice(0, this.formData.items.length, ...nuovoOrdine);
      this.riallineaPosizioni();
    },

    /* [sc-48248] Totale lordo di una suddivisione: netto applicato × quantità
       effettiva × aliquota, riga per riga. È il numero che dice di chi è il
       prezzo quando dentro al gruppo ci sono righe a zero — il caso del kit. */
    totaleSuddivisione(gruppo) {
      return (gruppo?.righe || []).reduce((somma, riga) => {
        const netto = Number(riga.price) || 0;
        const quantita = Number(this.rowQtyValue(riga)) || 0;
        const iva = Number(riga.iva ?? 22);
        return somma + netto * quantita * (1 + iva / 100);
      }, 0);
    },

    // [sc-49019] Totale di riga in tabella, IVA esclusa.
    totaleRigaImponibileFormattato,

    totaleSuddivisioneFormattato(gruppo) {
      return `€ ${formatDecimal(this.totaleSuddivisione(gruppo))}`;
    },

    /* [sc-48248] `position` deve raccontare quello che si vede: si rinumera
       seguendo l'ordine di lettura della tabella, gruppo per gruppo. */
    riallineaPosizioni() {
      let i = 1;
      for (const gruppo of this.gruppiArticoli) {
        for (const riga of gruppo.righe) riga.position = i++;
      }
    },

    goBack() {
      this.pendingNavigation = null;
      // Nessuna modifica (o sola lettura): torna indietro senza conferma.
      if (this.readOnly || !this.isDirty) {
        this.navigationConfirmed = true;
        this.$router.back();
        return;
      }
      this.showBackModal = true;
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        this.$router.back();
      }
    },
    // Guardia reload/chiusura tab nativa del browser: mostra il dialog
    // nativo di conferma quando si sta modificando (salta in sola lettura).
    handleBeforeUnload(e) {
      if (this.readOnly || !this.isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    },
    // sanitize stringhe nel payload prima dell'update
    sanitizeStr(s, { maxLen = 5000 } = {}) {
      if (s == null) return "";
      let out = String(s).trim();

      // rimuovi caratteri di controllo (tranne tab/newline)
      // out = out.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, " ");

      // strip tag HTML basilare
      out = out.replace(/<[^>]*>/g, " ");

      // normalizza spazi multipli
      out = out.replace(/\s{2,}/g, " ").replace(/\n{3,}/g, "\n\n");

      // limita lunghezza
      if (out.length > maxLen) out = out.slice(0, maxLen);

      return out;
    },
    cleanNumber(n, { min = null } = {}) {
      const v = Number(n);
      if (!Number.isFinite(v)) return 0;
      if (min != null && v < min) return min;
      return v;
    },

    // Riconosce se è piastrella (anche per Fuori Magazzino)
    isTilesObj(src) {
      return (
        src?.category === "piastrelle" ||
        (src?.category === "Fuori Magazzino" &&
          src?.subCategory === "piastrelle")
      );
    },

    // Nome da mostrare ovunque (tabella modale + PDF)
    nameForDisplay(src) {
      return this.itemDisplayFromObj(src || {}).title || "Sconosciuto";
    },

    // Capitallizza la prima lettera
    Cap(s) {
      return (s || "")
        .toString()
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    /**
     * Ritorna { title, meta } a partire dall'oggetto articolo già risolto
     * - Piastrelle (magazzino): titolo = itemName, meta = measureDesc (oppure WxH cm)
     * - Fuori Magazzino/Piastrelle: meta fisso "Piastrelle"
     * - Sanitari/Altro: meta = tipo/posizione (fallback subCategory)
     */
    itemDisplayFromObj(it = {}) {
      // --- GRUPPO AREA (piastrelle, rivestimenti, parquet, laminati, decking) ---
      if (getCategoryGroup(it.category) === "area") {
        const title = it.itemName || "Articolo";
        const dim =
          it.measureDesc ||
          it.formatLabel ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null);
        const thickStr = it.thicknessMm
          ? `${parseFloat(it.thicknessMm)}mm`
          : null;
        const meta =
          [
            dim && !title.includes(dim) ? dim : null,
            it.surface,
            thickStr && !title.includes(thickStr) ? thickStr : null,
            it.color,
          ]
            .filter(Boolean)
            .join(" · ") || it.category;
        return { title, meta };
      }

      // --- FUORI MAGAZZINO: PIASTRELLE ---
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const dim =
          it.measureDesc ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null);
        const title =
          [it.itemName, dim].filter(Boolean).join(" \u2192 ").trim() ||
          "Articolo fuori magazzino";
        return {
          title,
          meta: "Piastrelle",
        };
      }

      // --- SANITARI / ALTRO (STOCK) ---
      const title =
        it.itemName ||
        [it.brand, it.subBrand].filter(Boolean).join(" \u2192 ").trim() ||
        "Articolo fuori magazzino";

      // accetta anche eventuali chiavi legacy/type/position e usa category come fallback
      const T = this.Cap(it.fixtureType || it.type || it.category || "");
      const P =
        (it.fixturePosition || it.position) === "terra"
          ? "a terra"
          : this.Cap(it.fixturePosition || it.position || "");

      const metaParts = [T, P].filter(Boolean);
      const meta =
        (metaParts.length ? metaParts.join(" ") : null) ||
        this.Cap(it.subCategory || it.category || ""); // <- altro fallback

      return { title, meta };
    },
    /**
     * Ritorna { title, meta } cercando nel negozio partendo da id + hint categoria
     */
    displayForId(id, categoryHint) {
      if (!id) return { title: "Sconosciuto", meta: "" };
      const src = this.getItemFromStore(id, categoryHint) || {};
      if (src && src.id != null) return this.itemDisplayFromObj(src);

      // fallback: prova a leggere la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        return this.itemDisplayFromObj({
          id,
          category: row.category,
          itemName: row.itemName,
          brand: row.brand,
          subBrand: row.subBrand,
          subCategory: row.subCategory,
        });
      }
      return { title: "Sconosciuto", meta: "" };
    },

    // normalizza solo i campi "di valore" (UI effimera esclusa)
    normalizeForCompare() {
      const f = this.formData || {};
      const L = f.location || {};
      const locValid = (L.placeId || L.place_id) && L.latitude && L.longitude;
      const loc = locValid
        ? {
            placeId: L.placeId || L.place_id,
            lat: Number(L.latitude),
            lng: Number(L.longitude),
          }
        : null;

      const items = (f.items || [])
        .map((r) => {
          const id = r.itemId;
          const qty = Number(r.boxQuantity || r.pieces || 0);
          const price = Number(r.unitPrice ?? r.price ?? 0);
          const iva = Number(r.iva ?? 22);
          // [sc-48248] Nota, suddivisione e posizione fanno parte di quello che
          // si salva: senza, spostare un articolo in una suddivisione o
          // scrivergli una nota non risultava una modifica e «Aggiorna ed
          // esporta» restava spento.
          return {
            cat: r.category || "",
            id,
            qty,
            price,
            iva,
            note: r.note || "",
            sez: (r.sectionName || "").trim(),
            pos: r.position ?? null,
          };
        })
        .sort(
          (a, b) =>
            a.cat.localeCompare(b.cat) ||
            String(a.id || "").localeCompare(String(b.id || ""))
        );

      return {
        clientId:
          f.clientId === "" || f.clientId === null || f.clientId === undefined
            ? null
            : Number(f.clientId),
        date: f.date || "",
        payment_method: f.payment_method || "",
        technical_queries: f.technical_queries || "",
        hasPackagingFee: !!f.hasPackagingFee,
        deliveryType: f.deliveryType || "",
        location: loc,
        items,
      };
    },

    // normalizza la location arrivata dall’API a camelCase + latitude/longitude
    normalizeLocFromApi(raw) {
      if (!raw) {
        return {
          name: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
          link: "",
        };
      }
      const obj =
        typeof raw === "string"
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return {};
              }
            })()
          : raw;

      const placeId = obj.placeId || obj.place_id || "";
      // supporta lat/lng legacy o geometry di Google
      const latRaw =
        obj.latitude ?? obj.lat ?? obj?.geometry?.location?.lat ?? "";
      const lngRaw =
        obj.longitude ?? obj.lng ?? obj?.geometry?.location?.lng ?? "";
      let latitude =
        typeof latRaw === "function" ? Number(latRaw()) : Number(latRaw);
      let longitude =
        typeof lngRaw === "function" ? Number(lngRaw()) : Number(lngRaw);

      // fallback: estrae lat/lng dall'URL del link (formato query=lat%2Clng)
      if (
        (!latRaw ||
          !lngRaw ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)) &&
        obj.link
      ) {
        const m = obj.link.match(/[?&]query=([-\d.]+)(?:%2C|,)([-\d.]+)/i);
        if (m) {
          latitude = Number(m[1]);
          longitude = Number(m[2]);
        }
      }

      return {
        name: obj.name || "",
        address: obj.address || obj.formatted_address || "",
        placeId,
        latitude: Number.isFinite(latitude) ? latitude : "",
        longitude: Number.isFinite(longitude) ? longitude : "",
        link: obj.link || "",
      };
    },

    computeSignature() {
      // stringify stabile: gli oggetti sono già normalizzati e ordinati
      return JSON.stringify(this.normalizeForCompare());
    },
    captureSnapshot() {
      this.originalSig = this.computeSignature();
    },

    async loadDuplicateQuote() {
      const id = this.lastDuplicateQuoteInfo?.quoteId;
      if (!id) return;

      this.dupLoading = true;
      this.dupError = "";
      this.dupQuote = null;

      try {
        const { data } = await axiosInstance.get(`/quote/${id}`, {
          withCredentials: true,
        });

        // location normalizzata
        let parsedLocation = null;
        try {
          parsedLocation =
            typeof data?.quote?.location === "string"
              ? JSON.parse(data.quote.location)
              : data?.quote?.location || null;
        } catch {
          parsedLocation = null;
        }

        // items normalizzati per il modale
        const items = (data?.quote?.quote_items || [])
          .filter((qi) => !!qi.item)
          .map((qi) => {
            const cat = qi.item.category;
            const isBoxBased = isBoxBasedCategory(cat);
            return {
              category: cat,
              itemName: qi.item.itemName || qi.item.subBrand,
              brand: qi.item.subBrand,
              boxQuantity: isBoxBased ? qi.quantity : 0,
              pieces: isBoxBased ? 0 : qi.quantity,
              mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
              iva: qi.iva ?? 22,
              price: Number(qi.price ?? 0),
              discountedPrice:
                qi.discountedPrice != null ? Number(qi.discountedPrice) : null,
            };
          });

        this.dupQuote = {
          ...data.quote,
          location: parsedLocation,
          totalPrice: data?.quote?.totalPrice ?? null,
          items,
        };
        this.dupDocuments = data?.quote?.documents || [];
      } catch (e) {
        this.dupError =
          e?.response?.data?.message ||
          "Impossibile caricare il preventivo esistente.";
      } finally {
        this.dupLoading = false;
      }
    },

    openDuplicateModal(quoteId, address) {
      this.lastDuplicateQuoteInfo = {
        quoteId: quoteId ?? null,
        address: address ?? null,
      };
      this.dupError = "";
      this.dupQuote = null;
      this.showDuplicateModal = true;
      if (this.lastDuplicateQuoteInfo.quoteId) this.loadDuplicateQuote();
    },
    closeDuplicateModal() {
      this.showDuplicateModal = false;
    },

    number(v) {
      return (typeof v === "number" ? v : Number(v || 0)).toFixed(2);
    },
    money(v) {
      return `€ ${this.number(v)}`;
    },
    formatDocName(name) {
      return (
        (name ?? "")
          .toString()
          // prima togli eventuale ".dev" immediatamente prima di ".pdf"
          .replace(/\.dev(?=\.pdf$)/i, "")
          // poi rimuovi "_N_<numero>.pdf" in fondo
          .replace(/_N_\d+\.pdf$/i, "")
      );
    },
    formatToLocalDate,

    onChangeAddress() {
      this.showDuplicateModal = false;
      this.$nextTick(() => document.getElementById("autocomplete")?.focus());
    },

    // In Edit carico il preventivo in conflitto e NE COPIO i contenuti nella form attuale,
    // svuotando l'indirizzo: salverai poi con il tuo update
    async onDuplicateWithEdit() {
      const sourceId = this.lastDuplicateQuoteInfo?.quoteId;
      this.showDuplicateModal = false;
      if (!sourceId) return;

      try {
        if (!this.dupQuote || this.dupQuote.id !== sourceId) {
          this.dupLoading = true;
          const { data } = await axiosInstance.get(`/quote/${sourceId}`, {
            withCredentials: true,
          });
          let loc = data?.quote?.location;
          try {
            loc = typeof loc === "string" ? JSON.parse(loc) : loc;
          } catch (error) {
            this.$toast.error(
              "Impossibile caricare il dettaglio di questo preventivo."
            );
          }
          this.dupQuote = { ...data.quote, location: loc };
        }
        const q = this.dupQuote;

        // mappa quote_items -> formData.items (schema Edit)
        const mappedItems = (q.quote_items || [])
          .map((qi) => {
            if (qi.item) {
              const it = qi.item;
              const isBoxBased = isBoxBasedCategory(it.category);
              return {
                itemId: it.id,
                itemName: it.itemName || it.subBrand,
                category: it.category,
                boxQuantity: isBoxBased ? qi.quantity : 0,
                pieces: isBoxBased ? 0 : qi.quantity,
                mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
                price: Number(qi.price),
                discountedPrice:
                  qi.discountedPrice != null
                    ? Number(qi.discountedPrice)
                    : null,
                iva: qi.iva ?? 22,
              };
            }
            return null;
          })
          .filter(Boolean);

        // cache per etichette
        (q.quote_items || []).forEach((qi) => {
          // if (qi.item) this.selectedItemById[qi.item.id] = qi.item;
          // if (qi.outstock) this.selectedItemById[qi.outstock.id] = qi.outstock;
          if (qi.item) this.selectedByKey[KEY.STOCK(qi.item.id)] = qi.item;
        });

        // compila la form con i dati del preventivo duplicato, ma svuota l'indirizzo
        this.formData = {
          ...this.formData,
          clientId: q.clientId,
          payment_method: q.payment_method || "",
          technical_queries: q.technical_queries || "",
          items: mappedItems,
          location: {
            name: "",
            address: "",
            placeId: "",
            latitude: "",
            longitude: "",
          },
        };
        this.newItem = this.resetNewItem();

        this.$nextTick(() => document.getElementById("autocomplete")?.focus());
        this.$toast.info(
          `Contenuto del preventivo #${q.id} copiato. Inserisci un indirizzo diverso e salva.`
        );
      } catch (e) {
        this.$toast.error("Impossibile duplicare questo preventivo ora.");
      } finally {
        this.dupLoading = false;
      }
    },

    measureDescFrom(w, h) {
      const W = Number(w),
        H = Number(h);
      if (Number.isFinite(W) && Number.isFinite(H) && W > 0 && H > 0) {
        return `${W}×${H} cm`;
      }
      return null;
    },

    rowCategoryGroup(row) {
      const it = this.getItemFromStore(row.itemId, row.category);
      return getCategoryGroup(it?.category);
    },
    isStockTiles(row) {
      return this.rowCategoryGroup(row) === "area";
    },
    qtyCell(row) {
      const g = this.rowCategoryGroup(row);
      if (g === "area" || g === "linear") return Number(row.boxQuantity || 0);
      return Number(row.pieces || row.quantity || 0);
    },
    categoryHintForRow(row) {
      if (row?.category) return row.category;
      return "STOCK";
    },

    getItemFromStore(id) {
      if (!id) return {};

      const key = KEY.STOCK(id);

      // Store prima: inWarehouse sempre aggiornato rispetto alla cache del preventivo
      const stk =
        (this.items || []).find((i) => i.id === id) ||
        (this.filteredItems || []).find((i) => i.id === id);
      if (stk) {
        this.selectedByKey[key] = stk;
        return stk;
      }

      // Fallback alla cache per articoli non presenti nello store globale
      const cached = this.selectedByKey[key];
      if (cached && String(cached.id) === String(id)) {
        return cached;
      }

      return {};
    },

    // Mappa l'azione updateQuote dallo store
    ...mapActions([
      "fetchQuotes",
      "updateQuote",
      "createOrder",
      "uploadQuoteDocument",
      "sendQuoteDocumentEmail",
    ]),

    // formatToDateTimeLocal,

    itemSearchLabel(it) {
      if (!it) return "";
      return it?.itemName
        ? `${it.itemName} ${it.measureDesc || ""}`.trim()
        : `${it?.subBrand || ""} ${it?.fixtureType || ""} ${
            it?.fixturePosition || ""
          }`.trim();
    },

    // Riga secondaria del risultato ricerca: brand + formato + finiture.
    itemFormatFinish(it) {
      return [it?.brand, it?.formatLabel, it?.finish]
        .filter(Boolean)
        .join(" · ");
    },

    warehouseCount(item) {
      const stock = Number(item?.warehouse?.inWarehouse ?? item?.inWarehouse);
      return Number.isFinite(stock) ? stock : 0;
    },

    isOrderableOutOfStock(item) {
      return !!item?.id && this.warehouseCount(item) <= 0;
    },

    /* Giacenza mostrata nell'unità di vendita: mq/ml per gli articoli a
       scatola (le scatole restano nel tooltip), pezzi per gli altri. */
    /* Articolo venduto a scatola ma senza mq/ml per scatola: le scatole non
       sono calcolabili, quindi giacenza e quantità da ordinare non vengono
       mostrate (sarebbero numeri inventati). */
    hasBoxData(item) {
      const group = getCategoryGroup(item?.category);
      if (group !== "area" && group !== "linear") return true;
      return itemPerBox(item) > 0;
    },

    rowHasBoxData(row) {
      return this.hasBoxData(this.rowStockItem(row));
    },

    stockLabel(item) {
      const count = this.warehouseCount(item);
      const group = getCategoryGroup(item?.category);
      if (group !== "area" && group !== "linear") return `${count} sc.`;
      const perBox = itemPerBox(item);
      // senza mq/ml per scatola la giacenza in mq non è ricavabile
      if (!perBox) return "—";
      const unit = group === "linear" ? "ml" : "mq";
      return `${formatDecimal(quantityFromBoxes(count, perBox))} ${unit}`;
    },

    stockTitle(item) {
      const count = this.warehouseCount(item);
      const group = getCategoryGroup(item?.category);
      if (group === "linear")
        return itemPerBox(item)
          ? `${count} ${count === 1 ? "confezione" : "confezioni"} in magazzino`
          : "Giacenza non calcolabile: manca ML / confezione nella scheda articolo";
      if (group === "area")
        return itemPerBox(item)
          ? `${count} ${count === 1 ? "scatola" : "scatole"} in magazzino`
          : "Giacenza non calcolabile: manca M² / scatola nella scheda articolo";
      return `${count} pz in magazzino`;
    },

    isQtyExceedingStock(row) {
      const item = this.getItemFromStore(
        row.itemId,
        this.categoryHintForRow(row)
      );
      if (!item?.id) return false;
      const stock = this.warehouseCount(item);
      if (stock <= 0) return false;
      return this.qtyCell(row) > stock;
    },

    /* mq/ml scritti a mano per riga, indicizzati per itemId: il PDF li usa al
       posto della quantità ricavata dalle scatole. */
    mqOverridesForPdf() {
      const map = {};
      (this.formData.items || []).forEach((row) => {
        const mq = Number(row?.mqQuantity);
        if (row?.itemId && mq > 0) map[row.itemId] = mq;
      });
      return map;
    },

    /* mq/ml digitati nel form, se validi (altrimenti null: la riga userà
       l'equivalente delle scatole) */
    typedMqQuantity() {
      if (!this.showMqField) return null;
      const n = Number(this.mqInput);
      return n > 0 ? n : null;
    },

    /* Allinea il campo mq alla quantità già presente in newItem (es. quando si
       riseleziona un articolo che ha già una quantità impostata). */
    syncMqInputFromBoxes() {
      const perBox = perBoxOrFallback(this.mqPerBox);
      const boxes = Number(this.newItem.boxQuantity) || 0;
      this.mqInput = boxes ? quantityFromBoxes(boxes, perBox) : "";
    },

    /* mq/ml per scatola della riga: 0 solo se l'articolo non è venduto a
       scatola, altrimenti il dato dell'articolo (o 1 se manca) */
    rowPerBox(row) {
      const group = this.rowCategoryGroup(row);
      if (group !== "area" && group !== "linear") return 0;
      const it =
        this.getItemFromStore(row.itemId, this.categoryHintForRow(row)) || {};
      const perBox =
        group === "linear"
          ? Number(it.mlPerBox) || 0
          : Number(it.sqmPerBox) || 0;
      return perBoxOrFallback(perBox);
    },

    /* Unità della colonna quantità: mq/ml per gli articoli a scatola, pz per gli
       altri. Le scatole restano la quantità del preventivo ma non si mostrano. */
    rowQtyUnit(row) {
      const group = this.rowCategoryGroup(row);
      if (!this.rowPerBox(row)) return group === "linear" ? "ml" : "pz";
      return group === "linear" ? "ml" : "mq";
    },

    rowQtyStep(row) {
      return this.rowPerBox(row) ? "any" : "1";
    },

    /* Valore in colonna quantità: i mq/ml scritti dall'operatore (non riscritti),
       con fallback all'equivalente delle scatole; pezzi per gli altri articoli */
    rowQtyValue(row) {
      const perBox = this.rowPerBox(row);
      if (!perBox) return this.qtyCell(row);
      const typed = Number(row.mqQuantity);
      if (typed > 0) return row.mqQuantity;
      return quantityFromBoxes(Number(row.boxQuantity) || 0, perBox);
    },

    rowQtyTitle(row) {
      const perBox = this.rowPerBox(row);
      if (!perBox) return "Quantità in pezzi";
      // senza mq/ml per scatola le scatole non sono calcolabili: si dice
      // perché, invece di mostrare un numero inventato
      if (!this.rowHasBoxData(row)) {
        return "Scatole non calcolabili: completa la scheda articolo";
      }
      // scatole ricalcolate dai mq mostrati con il dato corrente dell'articolo:
      // boxQuantity salvato può essere vecchio (scheda completata dopo il
      // preventivo). È lo stesso calcolo che l'API rifà alla conferma.
      const requested = Number(this.rowQtyValue(row)) || 0;
      const boxes = requested > 0 ? boxesFromQuantity(requested, perBox) : 0;
      const isLinear = this.rowCategoryGroup(row) === "linear";
      const label =
        boxes === 1
          ? isLinear
            ? "confezione"
            : "scatola"
          : isLinear
          ? "confezioni"
          : "scatole";
      const unit = isLinear ? "ml" : "mq";
      const total = formatDecimal(quantityFromBoxes(boxes, perBox));
      return `${boxes} ${label} intere (${total} ${unit}) per i ${formatDecimal(
        requested
      )} ${unit} richiesti`;
    },

    /* [sc-48248] Nota libera del prodotto. Si vede se c'è, o se l'operatore
       ha aperto il campo per scriverla. */
    notaAperta(row) {
      return !!row?.note || !!this.noteAperte[row?.itemId];
    },

    toggleNota(row) {
      const id = row?.itemId;
      if (id == null) return;
      this.noteAperte = { ...this.noteAperte, [id]: !this.noteAperte[id] };
    },

    /* [sc-48248] Mentre si scrive il testo si tiene com'è, spazi compresi:
       ripulirlo a ogni tasto cancellerebbe lo spazio appena digitato. La
       ripulitura avviene quando il campo perde il fuoco (o con Invio, che lo
       fa perdere), in `updateRowNote`. */
    scriviNota(row, val) {
      row.note = val === "" ? null : val;
    },

    updateRowNote(row, val) {
      const testo = String(val ?? "").trim();
      row.note = testo || null;
    },

    /* [sc-48248] Prezzo della riga, editabile in tabella.
       Lo zero è ammesso: è il prezzo dei prodotti dentro un kit, che vengono
       elencati al cliente ma pagati con la riga del kit. */
    rowPriceValue(row) {
      const p = Number(row?.price);
      return Number.isFinite(p) ? p : 0;
    },

    updateRowPrice(row, val, inputEl = null) {
      const grezzo = String(val ?? "")
        .trim()
        .replace(",", ".");
      // Campo svuotato: `Number("")` è 0, e una riga diventerebbe gratuita per
      // aver cancellato il prezzo senza riscriverlo. Si rimette quello di prima.
      if (!grezzo) {
        if (inputEl) inputEl.value = this.rowPriceValue(row);
        return;
      }
      const n = Number(grezzo);
      if (!Number.isFinite(n) || n < 0) {
        this.$toast.error("Il prezzo non può essere negativo.");
        if (inputEl) inputEl.value = this.rowPriceValue(row);
        return;
      }
      row.price = +n.toFixed(2);
      // [sc-48248] Il prezzo scritto qui è IL prezzo applicato. Va azzerato
      // anche `discountedPrice`, che l'API preferisce a `price`
      // (`resolveAppliedNet`): sulle righe che arrivano da una duplica è
      // valorizzato, e senza questa riga la modifica non arriverebbe a DB —
      // compreso il prezzo 0 dei prodotti dentro un kit.
      row.discountedPrice = null;
      if (inputEl && Number(inputEl.value) !== row.price) {
        inputEl.value = row.price;
      }
    },

    /* [sc-48248] Aliquota della riga. Le voci ammesse sono quelle di
       `ivaOptions`, le stesse del form di inserimento e dell'ENUM a DB. */
    updateRowIva(row, val) {
      const n = Number(val);
      const ammesse = this.ivaOptions.map((o) => o.value);
      if (!ammesse.includes(n)) {
        this.$toast.error("Aliquota IVA non ammessa.");
        return;
      }
      row.iva = n;
    },

    updateRowQty(row, val, inputEl = null) {
      const group = this.rowCategoryGroup(row);
      const perBox = this.rowPerBox(row);
      const requested = Number(val) || 0;
      // per togliere l'articolo si usa il pulsante rimuovi, non la quantità 0
      if (requested <= 0) {
        this.$toast.error(
          "La quantità deve essere maggiore di 0: usa il pulsante rimuovi per eliminare l'articolo."
        );
        if (inputEl) inputEl.value = this.rowQtyValue(row);
        return;
      }
      if (perBox) {
        // l'operatore scrive i mq/ml: il valore resta quello digitato, le scatole
        // intere da ordinare sono derivate per il magazzino
        row.mqQuantity = requested;
        const boxes = Math.max(1, boxesFromQuantity(requested, perBox));
        row.boxQuantity = boxes;
        row.quantity = boxes;
        const storeItem = this.getItemFromStore(
          row.itemId,
          this.categoryHintForRow(row)
        );
        row.pieces = boxes * (storeItem?.piecesPerBox || 1);
        return;
      }
      const n = Math.max(1, Math.round(requested));
      if (group === "area" || group === "linear") {
        row.boxQuantity = n;
        const storeItem = this.getItemFromStore(
          row.itemId,
          this.categoryHintForRow(row)
        );
        row.pieces = n * (storeItem?.piecesPerBox || 1);
      } else {
        row.pieces = n;
      }
      // il binding :value non si aggiorna se il valore arrotondato coincide con
      // quello già in stato: riallineo il campo a mano
      if (inputEl && +inputEl.value !== n) inputEl.value = n;
    },

    /* Articolo della riga, risolto dallo store (le righe portano solo l'id) */
    rowStockItem(row) {
      const itemId = row?.itemId ?? row?.id;
      if (row?.id && row?.category) return row;
      return this.getItemFromStore(itemId, this.categoryHintForRow(row));
    },

    /* Riga con scheda articolo incompleta: si evidenzia in tabella, così è
       chiaro cosa completare prima di confermare il preventivo. */
    isRowStockIncomplete(row) {
      return hasIncompleteStockData(this.rowStockItem(row));
    },

    rowStockWarning(row) {
      const item = this.rowStockItem(row);
      const missing = missingStockFields(item);
      if (!missing.length) return "";
      return `Scheda articolo incompleta (manca ${missing.join(
        ", "
      )}): completala prima di confermare il preventivo.`;
    },

    validateQuoteItemsBeforeSubmit() {
      const rows = this.formData.items || [];
      // nessuna riga a quantità 0: il preventivo non deve poter essere salvato
      const zeroQtyRow = rows.find((row) => this.qtyCell(row) < 1);
      if (zeroQtyRow) {
        const label = this.displayForId(
          zeroQtyRow.itemId,
          this.categoryHintForRow(zeroQtyRow)
        ).title;
        this.errorMessage = `Quantità non valida per "${label}": inserisci almeno 1 o rimuovi l'articolo.`;
        this.$toast.error(this.errorMessage);
        return false;
      }
      return true;
    },

    isOrderQueued(item) {
      return !!item?.id && !!this.pendingOrderItems[item.id];
    },

    isOrderQueuedRow(row) {
      return !!row?.itemId && !!this.pendingOrderItems[row.itemId];
    },

    // Quantità da ordinare per una riga (scarto tra richiesta e giacenza)
    /* Quantità mancante espressa nell'unità della riga: per gli articoli a
       mq/ml le scatole da ordinare si traducono nei mq/ml corrispondenti
       (le scatole restano fra parentesi: è quello che si ordina davvero). */
    qtyToOrderLabel(row) {
      const boxes = this.qtyToOrder(row);
      const perBox = this.rowPerBox(row);
      const unit = this.rowQtyUnit(row);
      if (!perBox || unit === "pz") return `${boxes} pz`;
      const qty = formatDecimal(quantityFromBoxes(boxes, perBox));
      const isLinear = unit === "ml";
      const label =
        boxes === 1
          ? isLinear
            ? "confezione"
            : "scatola"
          : isLinear
          ? "confezioni"
          : "scatole";
      return `${qty} ${unit} (${boxes} ${label})`;
    },

    qtyToOrder(row) {
      const item = this.getItemFromStore(
        row.itemId,
        this.categoryHintForRow(row)
      );
      return this.orderQuantityForItem(item);
    },

    orderQuantityForItem(item) {
      if (!item?.id) return 1;

      const stock = this.warehouseCount(item);

      const row = (this.formData.items || []).find(
        (entry) => Number(entry?.itemId) === Number(item.id)
      );
      if (row) {
        const rg = getCategoryGroup(item.category);
        const qty =
          rg === "area" || rg === "linear"
            ? Number(row.boxQuantity || row.quantity || 0)
            : Number(row.pieces || row.quantity || 0);
        if (qty > 0) {
          return stock > 0 ? Math.max(qty - stock, 1) : qty;
        }
      }

      if (this.newItem.itemId === item.id) {
        const g = getCategoryGroup(item.category);
        const qty =
          g === "area" || g === "linear"
            ? Number(this.newItem.boxQuantity || 0)
            : Number(this.newItem.pieces || 0);
        if (qty > 0) return stock > 0 ? Math.max(qty - stock, 1) : qty;
      }
      return 1;
    },

    buildPendingOrderNote(quoteId) {
      return `Ordine per preventivo #${quoteId}`;
    },

    async processPendingOrders(quoteId) {
      const itemIds = Object.keys(this.pendingOrderItems || {}).map(Number);
      if (!quoteId || !itemIds.length) return;

      const results = await Promise.allSettled(
        itemIds.map((itemId) =>
          this.createOrder({
            itemId,
            quantity: this.orderQuantityForItem(this.getItemFromStore(itemId)),
            notes: this.buildPendingOrderNote(quoteId, itemId),
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        this.$toast.error(
          `${failed.length} carico/i merce non creato/i dopo il salvataggio del preventivo.`
        );
      } else {
        this.$toast.success(
          "Carichi merce collegati al preventivo creati con successo."
        );
      }
    },

    queueOrderFromQuoteItem(item) {
      if (!item?.id || this.isOrderQueued(item)) return;

      this.pendingOrderItems = {
        ...this.pendingOrderItems,
        [item.id]: true,
      };

      this.$toast.info(
        "Ordine messo in coda. Verrà creato al salvataggio del preventivo."
      );
    },

    unqueueOrderFromQuoteItem(item) {
      if (!item?.id || !this.isOrderQueued(item)) return;

      const next = { ...this.pendingOrderItems };
      delete next[item.id];
      this.pendingOrderItems = next;

      if (this.newItem.itemId === item.id) this.orderNote = "";

      this.$toast.info("Ordine rimosso dalla coda.");
    },

    selectItem(item) {
      this.newItem.itemId = item.id;
      this.orderNote = "";

      // Prezzo di listino NETTO (IVA esclusa), invariato al cambio IVA.
      const listNet = Number(item.price) || 0;
      this.searchQueryItem = this.itemSearchLabel(item);

      // cache namespaced
      this.selectedByKey[KEY.STOCK(item.id)] = item;

      // unitPrice = prezzo unitario netto
      this.newItem.price = listNet;
      this.newItem.unitPrice = listNet;

      // Prezzo scontato: inizialmente vuoto (l'operatore lo digita se serve).
      this.newItem.discountedPrice = "";

      // il campo mq deve riflettere la quantità dell'articolo appena scelto
      this.syncMqInputFromBoxes();
      this.hideDropdown();
    },

    selectClient(client) {
      this.formData.clientId = client.id;
      this.searchQueryClient = client.clientName;
      this.hideDropdown();
    },

    startSearch(type) {
      this.activeSearch = type; // 'client' o 'item'
      this.showDropdown = true;
      this.selectedIndex = -1;

      // se parte una nuova ricerca resetta l’oggetto che stavi "editando"
      if (type === "item") {
        this.newItem = this.resetNewItem();
      }
      if (type === "client") this.searchQueryClient = "";
      if (type === "item") this.searchQueryItem = "";
    },

    handleKey(event) {
      if (!this.showDropdown) return;

      // Quale array devo navigare?
      let list;
      if (this.activeSearch === "client") {
        list = this.filteredClients;
      } else if (this.activeSearch === "item") {
        list = this.filteredItems;
      } else {
        list = [];
      }

      // ========== freccia giù ==========
      if (event.key === "ArrowDown") {
        this.selectedIndex =
          this.selectedIndex < list.length - 1 ? this.selectedIndex + 1 : 0;
        event.preventDefault();

        // ========== freccia su ==========
      } else if (event.key === "ArrowUp") {
        this.selectedIndex =
          this.selectedIndex > 0 ? this.selectedIndex - 1 : list.length - 1;
        event.preventDefault();

        // ========== ENTER ==========
      } else if (event.key === "Enter") {
        if (this.selectedIndex >= 0 && this.selectedIndex < list.length) {
          const chosen = list[this.selectedIndex];

          if (this.activeSearch === "client") {
            // selezione cliente
            this.formData.clientId = chosen.id;
            this.searchQueryClient = chosen.clientName;
          } else if (this.activeSearch === "item") {
            // selezione articolo di magazzino
            this.newItem.itemId = chosen.id;
            this.searchQueryItem = chosen.itemName
              ? `${chosen.itemName} ${chosen.measureDesc || ""}`.trim()
              : `${chosen.subBrand || ""} ${chosen.fixtureType || ""} ${
                  chosen.fixturePosition || ""
                }`.trim();

            // cache namespaced
            this.selectedByKey[KEY.STOCK(chosen.id)] = chosen;

            // delega tutta la logica a onSelectItem (prezzo, iva, reset, ecc.)
            this.onSelectItem();
          }
          this.showDropdown = false;
          this.selectedIndex = -1;
        }
        event.preventDefault();
      }
    },

    hideDropdown() {
      // Usa un timeout per permettere il click di selezione sul dropdown prima di nasconderlo
      setTimeout(() => {
        this.showDropdown = false;
        this.selectedIndex = -1;
        this.activeSearch = null; // Aggiungi questa linea per resettare l'indice
      }, 300);
    },

    /* Numero opzionale non negativo: preserva lo zero (riga gratuita). Ritorna
       null per assente/vuoto/non numerico o negativo. */
    optionalNonNegativeNumber(value) {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    },

    /**
     * Gestisce la selezione di un articolo dal dropdown.
     * Chiude il dropdown e aggiorna la searchQuery con il nome dell'articolo selezionato.
     */
    // async onSelectItem() {
    //   this.hideDropdown();

    //   const chosenId = this.newItem.itemId;
    //   const item = this.getItemFromStore(chosenId, "STOCK");
    //   if (!item.id) return;

    //   // reset "pulito", ma con la stessa IVA
    //   const iva = this.newItem.iva || 22;
    //   this.newItem = this.resetNewItem();
    //   this.newItem.itemId = chosenId;
    //   this.newItem.iva = iva;
    //   this.newItem.category = item.category || "";

    //   // disaccoppia l’altra selezione
    //   this.formData.outstockId = null;
    //   this.searchQueryOutstock = "";

    //   this.selectedItemById[item.id] = item;
    //   this.searchQueryItem = item.itemName
    //     ? `${item.itemName} ${item.measureDesc || ""}`.trim()
    //     : `${item.subBrand || ""} ${item.fixtureType || ""} ${
    //         item.fixturePosition || ""
    //       }`.trim();

    //   const prezzoIvato = this.adjustedPrice(item.price, this.newItem.iva);
    //   this.newItem.price = prezzoIvato;
    //   this.newItem.unitPrice = prezzoIvato;
    // },

    async onSelectItem() {
      this.hideDropdown();
      const chosenId = this.newItem.itemId;
      const item = this.getItemFromStore(chosenId, "STOCK");
      if (!item.id) return;

      const iva = this.newItem.iva ?? 22;
      this.newItem = this.resetNewItem();
      this.newItem.itemId = chosenId;
      this.newItem.iva = iva;
      this.newItem.category = item.category || "";

      this.selectedByKey[KEY.STOCK(item.id)] = item;
      this.searchQueryItem = item.itemName
        ? `${item.itemName} ${item.measureDesc || ""}`.trim()
        : `${item.subBrand || ""} ${item.fixtureType || ""} ${
            item.fixturePosition || ""
          }`.trim();

      // Prezzo di listino NETTO (IVA esclusa).
      const listNet = Number(item.price) || 0;
      this.newItem.price = listNet;
      this.newItem.unitPrice = listNet;
    },

    handleClientSelection() {
      this.showDropdown = false;
      this.searchQueryClient = this.getClientName(this.formData.clientId);
    },
    /**
     * Recupera i dati del preventivo da modificare.
     * Viene chiamato appena il componente è montato.
     */
    async fetchQuoteData() {
      try {
        this.errorMessage = "";
        const response = await axiosInstance.get(`/quote/${this.quoteId}`);
        // Adatta il percorso a seconda della tua API
        if (response.status === 200) {
          const quoteData = response.data.quote; // notare che i dati stanno sotto `quote`
          this.modificaChiusa = ordineNonModificabile(quoteData);
          // console.log("fetchQuoteData quoteData", quoteData);
          // console.log("quoteData.response.data.quote", quoteData);
          // items viene dal server con item + item.QuoteItem
          // Creiamo un array "flat" con le proprietà necessarie
          // console.log("quoteData.quote_items", quoteData.quote_items);
          const mappedItems = (quoteData.quote_items || [])
            .map((qi) => {
              if (qi.item) {
                // ───── articolo di magazzino ─────
                const it = qi.item;
                const isBoxBased = isBoxBasedCategory(it.category);

                return {
                  itemId: it.id,
                  itemName: it.itemName || it.subBrand,
                  category: it.category,
                  boxQuantity: isBoxBased ? qi.quantity : 0,
                  pieces: isBoxBased ? 0 : qi.quantity,
                  mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
                  // Netto applicato: scontato storico se presente, altrimenti il
                  // prezzo netto della riga (nuova semantica IVA esclusa). Al
                  // salvataggio verrà inviato come price con discountValue null.
                  price:
                    qi.discountedPrice != null
                      ? Number(qi.discountedPrice)
                      : Number(qi.price),
                  discountedPrice: null,
                  iva: qi.iva,
                  // [sc-48248] nota, suddivisione e ordine viaggiano con la riga
                  note: qi.note || null,
                  sectionName: qi.sectionName || null,
                  position: qi.position ?? null,
                };
              } else {
                return null;
              }
            })
            .filter(Boolean);

          // ⚠️ subito dopo const mappedItems = ...
          (quoteData.items || []).forEach((it) => {
            // sono articoli di MAGAZZINO
            if (it && it.id != null) {
              this.selectedByKey[KEY.STOCK(it.id)] = it;
            }
          });

          (quoteData.quote_items || []).forEach((qi) => {
            if (qi.item && qi.item.id != null) {
              // stock
              this.selectedByKey[KEY.STOCK(qi.item.id)] = {
                ...qi.item,
                // in certi dump itemName è "", ma brand/subBrand ci sono
                // non forziamo qui: ci pensa nameForDisplay al fallback
              };
            }
          });

          this.formData.items = mappedItems;
          // console.log("this.formData.items", this.formData.items);
          this.formData.date = toDateLocal(quoteData.date);

          // this.formData.date = this.formatToDateTimeLocal(quoteData.date);
          this.formData.clientId = quoteData.clientId || "";
          const c = (this.clients || []).find(
            (x) => x.id === this.formData.clientId
          );
          this.searchQueryClient = c
            ? this.getClientLabel(c)
            : quoteData.clientName || "";
          // this.searchQueryClient = quoteData.clientName || "";
          // this.formData.operatorId = quoteData.operatorId || "";

          // this.formData.eventName = quoteData.eventName || ""; // Se non esiste, assegna una stringa vuota

          this.formData.location = this.normalizeLocFromApi(quoteData.location);

          this.formData.payment_method = quoteData.payment_method;
          this.formData.technical_queries = quoteData.technical_queries || ""; // Se non esiste, assegna una stringa vuota
          this.formData.hasPackagingFee = quoteData.hasPackagingFee ?? false; // addebito spesa imballo
          // Modalità consegna/ritiro: usa il valore salvato; per preventivi
          // vecchi senza il campo, deduci dalla presenza indirizzo.
          this.formData.deliveryType =
            quoteData.deliveryType ||
            (this.formData.location?.address ? "consegna" : "");

          const existingDocs = quoteData.documents || [];
          if (existingDocs.length > 0) {
            this.pdfUploaded = true;
            this.lastUploadedDocName = existingDocs.at(-1).name;
          }

          this.$nextTick(() => {
            this.successMessage = `Dati del preventivo con ID:${this.quoteId} caricati con successo`;
            this.$toast.success(this.successMessage);
            this.captureSnapshot(); // stato iniziale caricato
          });
        }
      } catch (error) {
        // console.error("fetchQuoteData error:", error);
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    toggleItemsList() {
      this.panelVisible = !this.panelVisible;
    },

    addOrUpdateItem() {
      this.errorMessage = "";
      this.successMessage = "";

      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";

      const qty = isAreaOrLinear
        ? Number(this.newItem.boxQuantity || 0)
        : Number(this.newItem.pieces || 0);

      if (!this.newItem.itemId || qty <= 0 || !this.newItem.unitPrice) {
        this.$nextTick(() => {
          this.errorMessage =
            "Compila tutti i campi prima di aggiungere un articolo.";
          this.$toast.error(this.errorMessage);
        });
        return;
      }

      // Prezzo NETTO applicato: scontato se valorizzato (zero incluso),
      // altrimenti il listino netto. Nessuno sconto inviato al backend.
      const personalized = this.optionalNonNegativeNumber(
        this.newItem.discountedPrice
      );
      const appliedNet =
        personalized !== null
          ? personalized
          : Number(this.newItem.unitPrice) || 0;

      const full = this.getItemFromStore(
        this.newItem.itemId,
        this.categoryHintForRow(this.newItem)
      );
      if (full.id) this.selectedByKey[KEY.STOCK(full.id)] = full;

      const existing = this.formData.items.find(
        (r) => r.itemId === this.newItem.itemId
      );

      if (existing) {
        if (isAreaOrLinear) {
          existing.boxQuantity = this.newItem.boxQuantity;
          existing.pieces = 0;
          existing.mqQuantity = this.typedMqQuantity();
        } else {
          existing.pieces = this.newItem.pieces;
        }
        existing.unitPrice = appliedNet;
        existing.price = appliedNet;
        existing.quantity = existing.pieces || existing.boxQuantity;
        existing.iva = this.newItem.iva;

        this.$nextTick(() => {
          this.successMessage = "Quantità aggiornata per l'articolo esistente.";
          this.$toast.success(this.successMessage);
        });
        return;
      }

      // nuova riga
      this.formData.items.push({
        itemId: this.newItem.itemId,
        category: this.newItem.category || this.selectedItem.category,
        boxQuantity: isAreaOrLinear ? this.newItem.boxQuantity : 0,
        pieces: isAreaOrLinear ? 0 : this.newItem.pieces,
        // mq/ml scritti dall'operatore: campo di sola UI, non inviato all'API
        mqQuantity: isAreaOrLinear ? this.typedMqQuantity() : null,
        quantity: isAreaOrLinear
          ? this.newItem.boxQuantity
          : this.newItem.pieces,
        // prezzo netto applicato (personalizzato se valorizzato, altrimenti listino)
        unitPrice: appliedNet,
        price: appliedNet,
        iva: this.newItem.iva,
      });

      this.$nextTick(() => {
        this.successMessage = "Articolo aggiunto con successo.";
        this.$toast.success(this.successMessage);
      });

      this.newItem = this.resetNewItem();
      this.searchQueryItem = "";
      this.showDropdown = false;
      // this.newItem.itemId = null;
      // this.formData.outstockId = null;
    },

    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    saveToLocalStorage(key, data) {
      localStorage.setItem(key, JSON.stringify(data));
    },

    loadFromLocalStorage(key) {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        return JSON.parse(savedData);
      }
      return null;
    },

    getItemName(id, category) {
      if (!id) return "Sconosciuto";
      const src = this.getItemFromStore(id, category);
      if (src && src.id != null) return this.nameForDisplay(src);

      // 🔚 ultima spiaggia: usa la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        // per sanitari/altro la mappatura include almeno itemName (o subBrand)
        return row.itemName || "Sconosciuto";
      }
      return "Sconosciuto";
    },

    getClientName(clientId) {
      const client = this.clients.find((c) => c.id === clientId);
      return client ? client.clientName : "Sconosciuto";
    },

    getClientLabel(c) {
      if (!c) return "";
      if (c.email) return `${c.clientName} - (${c.email})`;
      if (c.pec) return `${c.clientName} - (${c.pec})`;
      return c.clientName;
    },

    // [sc-48246] L'invio non parte più dal click sul bottone: prima si conferma.
    askSendDocumentEmail() {
      this.showEmailModal = true;
    },
    closeEmailModal() {
      if (this.sendingEmail) return;
      this.showEmailModal = false;
    },
    async confirmSendDocumentEmail() {
      try {
        await this.onSendDocumentEmail();
      } finally {
        this.showEmailModal = false;
      }
    },

    formatIva(iva) {
      const percentageSymbol = "%";
      return `${iva} ${percentageSymbol}`;
    },

    formatPrice(price) {
      // console.log("beforeFormatPrice price", price);
      const currencySymbol = "€"; // Cambia in "$" per il dollaro
      return `${currencySymbol} ${formatDecimal(price)}`;
    },

    unitForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};
      if (
        String(row?.itemName || it?.itemName || "")
          .toLowerCase()
          .includes("battiscopa")
      )
        return "€/mL";
      const g = getCategoryGroup(it.category);
      if (g === "area") return "€/mq";
      if (g === "linear") return "€/ml";
      return "€/pz";
    },

    // mqForRow(row) {
    //   // console.log("row", row);
    //   const item = this.getItemFromStore(
    //     row.itemId,
    //     row.category
    //   );
    //   // console.log("mqForRow item", item);
    //   // solo piastrelle
    //   if (item.category !== "piastrelle" && item.subCategory === "piastrelle") {
    //     return row.pieces;
    //   }
    //   const sqmPerBox = Number(item.sqmPerBox) || 0;
    //   const boxes = Number(row.boxQuantity) || 0;
    //   const total = sqmPerBox * boxes;
    //   return total ? total.toFixed(2) : "";
    // },

    mqForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};

      // Stock piastrelle: scatole × mqPerBox
      if (it.category === "piastrelle") {
        const sqmPerBox = Number(it.sqmPerBox) || 0;
        const boxes = Number(row.boxQuantity) || 0;
        const total = sqmPerBox * boxes;
        return total ? total.toFixed(2) : "N/D";
      }

      // Fuori magazzino piastrelle: i mq coincidono con 'pieces'
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const sqm = Number(row.pieces) || 0;
        return sqm ? sqm.toFixed(2) : "N/D";
      }

      // Altri casi
      return "N/D";
    },

    resetNewItem() {
      return {
        itemId: null,
        boxQuantity: 0,
        pieces: 0,
        price: "",
        discountedPrice: "",
        category: "",
        quantity: 0, // quantità pezzi per articoli fuori magazzino
        iva: 0,
      };
    },

    async onUpdateQuoteAndGeneratePdf() {
      this.errorMessage = "";
      this.successMessage = "";

      // [sc-48735] Anche l'invio del form con Invio passa di qui, non solo il
      // pulsante spento: l'API rifiuterebbe comunque con un 409.
      if (this.modificaChiusa) {
        this.$toast.error(this.testoModificaChiusa);
        return;
      }

      // 0) Validazione base
      if (this.formData.items.length === 0) {
        this.$nextTick(() => {
          this.errorMessage = "Aggiungi almeno un articolo prima di salvare";
          this.$toast.error(this.errorMessage);
        });
        return;
      }
      if (!this.formData.deliveryType) {
        this.$nextTick(() => {
          this.errorMessage =
            "Seleziona la modalità: consegna o ritiro, prima di salvare";
          this.$toast.error(this.errorMessage);
        });
        return;
      }

      try {
        // 1) pulizia item "in input" se presente
        if (
          this.newItem.itemId &&
          (this.newItem.boxQuantity ||
            this.newItem.pieces ||
            this.newItem.quantity)
        ) {
          this.formData.items.push({
            ...this.newItem,
            mqQuantity: this.typedMqQuantity(),
          });
        }

        if (!this.validateQuoteItemsBeforeSubmit()) return;

        // 2) costruisci items per API con coercizioni sicure
        const apiItems = this.formData.items.map((row) => {
          const qty = this.cleanNumber(row.boxQuantity || row.pieces, {
            min: 0,
          });
          const mq = Number(row.mqQuantity);
          return {
            itemId: row.itemId,
            quantity: qty,
            // mq/ml digitati: base di calcolo dell'importo lato API
            mqQuantity: mq > 0 ? mq : null,
            price: this.cleanNumber(row.unitPrice ?? row.price, { min: 0 }), // prezzo unitario ivato
            // prezzo finale ivato per riga; discountValue calcolato lato server
            discountedPrice:
              row.discountedPrice != null
                ? this.cleanNumber(row.discountedPrice, { min: 0 })
                : null,
            iva: this.cleanNumber(row.iva ?? 22, { min: 0 }),
            category: row.category || "",
            // [sc-48248] campi liberi della riga: l'API li ripulisce lei
            note: row.note || null,
            sectionName: row.sectionName || null,
            position: row.position ?? null,
          };
        });

        // 3) payload base
        let payload = {
          ...this.formData,
          items: apiItems,
        };

        // 4) normalizza data: type="date" dà YYYY-MM-DD, il backend vuole ISO con ora locale
        if (payload.date && payload.date.length === 10) {
          payload.date = new Date(payload.date + "T00:00:00").toISOString();
        }

        // 5) sanitize campi testuali
        payload.payment_method = (payload.payment_method || "").trim();
        payload.technical_queries = this.sanitizeStr(
          payload.technical_queries,
          { maxLen: 8000 }
        );

        // 5) location opzionale + normalizzazione (indirizzo in testo libero)
        if (payload.location) {
          payload.location = this.normalizeLocFromApi(payload.location);
          const L = payload.location || {};
          if (!(L.address || "").trim()) {
            delete payload.location; // se non c'è indirizzo, rimuovi
          }
        }

        // 6) client coerente
        payload.clientId =
          payload.clientId === "" || payload.clientId == null
            ? null
            : Number(payload.clientId);

        // 7) rimuovi duplicati nel payload (difesa ulteriore)
        const keyOf = (r) => `${r.itemId ?? ""}`;
        const seen = new Set();
        payload.items = payload.items.filter((r) => {
          const k = keyOf(r);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });

        // console.log("PAYLOAD:", JSON.stringify(payload, null, 2));

        // => Update preventivo
        const response = await this.updateQuote({
          quoteId: this.quoteId,
          payload,
        });
        this.response = response;
        // console.log("this.response", this.response);

        if (response?.status !== 200 && response?.status !== 201) {
          // ⬇️ ramo 409
          if (
            response?.status === 409 &&
            response?.data.code === "QUOTE_DUPLICATE"
          ) {
            const d = response?.data || {}; // usa quello che hai a disposizione
            const details = (typeof d.details === "object" && d.details) || {};
            const id = details.quoteId ?? d.quoteId ?? null;
            const address =
              details.address ?? this.formData?.location?.address ?? null;
            const msg =
              d.message ||
              d.details ||
              "Esiste già un preventivo per questo cliente allo stesso indirizzo in corso...";

            this.$toast.error(msg);
            this.openDuplicateModal(id, address);
            return;
          }

          // ...altri errori
          this.errorMessage =
            response.data?.details ||
            response.data?.message ||
            "Impossibile aggiornare il preventivo.";
          this.$toast.error(this.errorMessage);
          this.successMessage = "";
          return;
        }

        // 4) Aggiorna stato locale post-update
        const { updatedQuote, totalPrice } = response.data.quote;
        await this.processPendingOrders(
          Number(updatedQuote?.id || this.quoteId)
        );
        this.formData.totalPrice = Number(totalPrice);

        // Rimappa gli items per la tabella
        this.formData.items = (updatedQuote.quote_items || [])
          .map((qi) => {
            if (!qi.item) return null;
            const it = qi.item;
            const isBoxBased = isBoxBasedCategory(it.category);
            return {
              itemId: it.id,
              itemName: it.itemName || it.subBrand,
              category: it.category,
              boxQuantity: isBoxBased ? qi.quantity : 0,
              pieces: isBoxBased ? 0 : qi.quantity,
              mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
              sqmPerBox:
                getCategoryGroup(it.category) === "area"
                  ? Number(it.sqmPerBox) || 0
                  : 0,
              price: Number(qi.price),
              discountedPrice:
                qi.discountedPrice != null ? Number(qi.discountedPrice) : null,
              iva: qi.iva,
              // [sc-48248] nota, suddivisione e ordine come li ha salvati l'API
              note: qi.note || null,
              sectionName: qi.sectionName || null,
              position: qi.position ?? null,
            };
          })
          .filter(Boolean);

        // console.log(
        //   "QUOTE_ITEMS_FROM_API:",
        //   response?.data?.quote?.updatedQuote?.quote_items
        // );

        await this.$nextTick();
        this.captureSnapshot(); // stato aggiornato salvato

        this.$nextTick(() => {
          this.successMessage =
            response.data.message || "Preventivo aggiornato con successo!";
          this.$toast.success(this.successMessage);
        });

        // 5) Genera + carica + salva il PDF
        const isDev =
          process.env.NODE_ENV === "development" ||
          import.meta?.env?.MODE === "development" ||
          process.env?.VUE_APP_NODE_ENV === "development";
        await generateQuotePdf({
          quote: updatedQuote,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: this.isOrderRoute,
          mqOverrides: this.mqOverridesForPdf(),
          onComplete: () => {
            this.pdfUploaded = true;
            this.lastUploadedDocName = `Preventivo_N_${updatedQuote.id}${
              isDev ? ".dev" : ""
            }.pdf`;
            this.newItem = this.resetNewItem();
            this.orderNote = "";
            this.pendingOrderItems = {};
          },
        });

        // 6) Redirect alla lista (preventivi o ordini) dopo l'aggiornamento
        setTimeout(() => {
          this.navigationConfirmed = true;
          this.$router.push({ name: this.isOrderRoute ? "ordini" : "quotes" });
        }, 4000);
      } catch (error) {
        // console.error("Errore durante l'aggiornamento del preventivo:", error);
        const resp = error?.response;

        // ⬇️ ramo 409
        if (resp?.status === 409 && resp?.data?.code === "QUOTE_DUPLICATE") {
          const d = resp?.data || {}; // usa quello che hai a disposizione
          const details = (typeof d.details === "object" && d.details) || {};
          const id = details.quoteId ?? d.quoteId ?? null;
          const address =
            details.address ?? this.formData?.location?.address ?? null;
          const msg =
            d.message ||
            d.details ||
            "Esiste già un preventivo per questo cliente allo stesso indirizzo in corso...";

          this.$toast.error(msg);
          this.openDuplicateModal(id, address);
          return;
        }

        // ...altri errori
        this.errorMessage =
          resp?.data?.message ||
          resp?.data?.details ||
          resp?.data?.error ||
          "Errore durante l'aggiornamento del preventivo.";
        this.$toast.error(this.errorMessage);
        this.successMessage = "";
      }
    },

    async onSendDocumentEmail() {
      this.sendingEmail = true;
      try {
        await this.sendQuoteDocumentEmail({
          quoteId: this.quoteId,
          objectName: this.lastUploadedDocName,
        });
        this.$toast.success("Email inviata al cliente con successo.");
      } catch (err) {
        const msg =
          err.response?.data?.error || "Errore durante l'invio dell'email.";
        this.$toast.error(msg);
      } finally {
        this.sendingEmail = false;
      }
    },

    resetLocation() {
      this.formData.location = {
        name: "",
        address: "",
        placeId: "",
        latitude: "",
        longitude: "",
      };
    },

    // Prima ricerca (nuova query): pagina 1, svuota subito i risultati e reset
    // su 404/errore. `reqId` progressivo: le risposte obsolete (digitazione
    // rapida o cambio pagina in volo) vengono ignorate.
    fetchItems(q) {
      const query = (q || "").trim();
      const reqId = ++this.itemsRequestId;
      this.itemsSearchQuery = query;
      this.itemsPage = 1;
      this.itemsRequestedPage = null;
      this.itemsTotalPages = 1;
      this.itemsTotalItems = null;
      // I risultati della ricerca precedente non devono restare visibili né
      // selezionabili durante il caricamento della nuova query.
      this.filteredItems = [];
      this.requestItemsPage(query, 1, reqId, true);
    },
    // Cambio pagina (stessa query): imposta subito la pagina richiesta
    // (pending), poi la carica. Un click su un'altra pagina mentre una è in
    // volo la "supera" (nuovo reqId); i doppioni verso la pagina già richiesta o
    // già caricata sono ignorati. I risultati sostituiscono, non concatenano.
    onItemPageChange(page) {
      const target = Number(page);
      if (!Number.isInteger(target)) return;
      if (target < 1 || target > this.itemsTotalPages) return;
      const active =
        this.itemsRequestedPage != null
          ? this.itemsRequestedPage
          : this.itemsPage;
      if (target === active) return; // niente richieste duplicate
      this.itemsRequestedPage = target;
      const reqId = ++this.itemsRequestId;
      this.requestItemsPage(this.itemsSearchQuery, target, reqId, false);
    },
    // GET condivisa. `isFirstSearch` distingue la gestione errori. Una risposta
    // obsoleta (reqId superato) esce subito senza toccare risultati, pagina o
    // stato loading/pending.
    requestItemsPage(query, page, reqId, isFirstSearch) {
      this.itemsLoading = true;
      axiosInstance
        .get("/item/get-all", { params: { page, limit: 10, search: query } })
        .then(({ data }) => {
          if (reqId !== this.itemsRequestId) return; // risposta obsoleta
          this.filteredItems = data.results || [];
          const p = data.pagination || {};
          this.itemsPage = p.currentPage || page;
          this.itemsTotalPages = p.totalPages || 1;
          this.itemsTotalItems = p.totalItems ?? null;
          this.itemsRequestedPage = null; // pagina caricata: pending azzerato
        })
        .catch((err) => {
          if (reqId !== this.itemsRequestId) return; // risposta obsoleta
          const is404 = err?.response?.status === 404;
          if (!is404) console.error(err);
          if (isFirstSearch) {
            // Nessun risultato / errore in prima ricerca: lista vuota, pagina 1.
            this.filteredItems = [];
            this.itemsPage = 1;
            this.itemsTotalPages = 1;
            this.itemsTotalItems = is404 ? 0 : null;
          }
          // Cambio pagina fallito: si mantengono risultati e itemsPage caricati;
          // si azzera solo la pagina richiesta e si riabilitano i controlli.
          this.itemsRequestedPage = null;
        })
        .finally(() => {
          if (reqId === this.itemsRequestId) this.itemsLoading = false;
        });
    },
    // --- pulizia selezione articolo di magazzino ---
    clearItemSelection() {
      const iva = this.newItem.iva ?? 22; // preserva l'IVA scelta (anche 0%)
      Object.assign(this.newItem, this.resetNewItem(), { iva });
      this.searchQueryItem = "";
      this.orderNote = "";
    },

    // Apertura modale "Crea articolo".
    openCreateItemModal() {
      console.log("openCreateItemModal"); // DEBUG temporaneo
      this.showCreateItemModal = true;
    },

    // Articolo creato dalla modale: lo rende disponibile nella ricerca e lo
    // auto-seleziona riusando la logica esistente selectItem().
    async onItemCreated({ item, itemCode, message }) {
      let createdItem = item;

      // Fallback robusto: se la response non è completa, rileggo l'articolo
      // con lo stesso endpoint/param (search) usato dalla ricerca del preventivo.
      if (!createdItem || createdItem.id == null || createdItem.price == null) {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: { page: 1, limit: 10, search: itemCode },
        });
        const results = data?.results || [];
        createdItem =
          results.find(
            (it) => it.itemCode === itemCode || it.item_code === itemCode
          ) ||
          results[0] ||
          createdItem;
      }

      if (!createdItem?.id) {
        this.$toast.error("Articolo creato ma non recuperabile.");
        return;
      }

      if (!this.filteredItems.some((it) => it.id === createdItem.id)) {
        this.filteredItems = [createdItem, ...this.filteredItems];
      }

      this.selectItem(createdItem);
      this.showCreateItemModal = false;
      this.$toast.success(message || "Articolo creato e selezionato");
    },
  },

  mounted() {
    this.panelVisible = window.innerWidth >= 768;
    this.fetchQuoteData();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  watch: {
    clients(newList) {
      if (this.formData.clientId && !this.searchQueryClient) {
        const c = newList.find((x) => x.id === this.formData.clientId);
        if (c) this.searchQueryClient = this.getClientLabel(c);
      }
    },

    showDuplicateModal(v) {
      if (v && this.lastDuplicateQuoteInfo?.quoteId) this.loadDuplicateQuote();
    },
    lastDuplicateQuoteInfo: {
      deep: true,
      handler(nv) {
        if (this.showDuplicateModal && nv?.quoteId) this.loadDuplicateQuote();
      },
    },

    // 🔎 Articoli di magazzino
    searchQueryItem(newQ, oldQ) {
      if (oldQ && !newQ.trim() && this.newItem.itemId)
        this.clearItemSelection();
      this.debouncedFetchItems(newQ.trim());
    },

    // 🔒 mutua esclusione (come già facevi)
    // Deselezione articolo (addItem, startSearch, reset form): svuota anche il
    // campo mq

    "newItem.itemId"(v) {
      if (!v) this.mqInput = "";
    },

    // "formData.outstockId"(v) {
    //   if (!v) return;
    //   this.newItem.outstockId = v;
    //   this.newItem.itemId = null;
    // },

    // 💶 ricalcolo prezzo al cambio IVA (invariato salvo minimi ritocchi)
    // "newItem.iva"(val) {
    //   if (this.newItem.itemId) {
    //     const base = this.getItemFromStore(this.newItem.itemId).price;
    //     this.newItem.price = this.adjustedPrice(base, val);
    //     this.newItem.unitPrice = this.newItem.price;
    //     return;
    //   }
    //   if (this.newItem.outstockId) {
    //     const outstock = this.getItemFromStore(
    //       this.newItem.outstockId,
    //       "Fuori Magazzino"
    //     );
    //     const base =
    //       outstock.subCategory === "piastrelle"
    //         ? outstock.sqmPrice
    //         : outstock.price;
    //     this.newItem.unitPrice = Number(this.adjustedPrice(base, val));
    //     const qty = this.newItem.pieces || 0;
    //     this.newItem.price = (this.newItem.unitPrice * qty).toFixed(2);
    //   }
    // },

    /* mq/ml digitati -> scatole intere del preventivo (arrotondamento per
       eccesso). Il campo resta com'è stato scritto: nessuna riscrittura.
       Passa da quantityInput per riusare la logica prezzo (Fuori Magazzino). */
    mqInput(v) {
      if (!this.showMqField) return;
      this.quantityInput = boxesFromQuantity(
        v,
        perBoxOrFallback(this.mqPerBox)
      );
    },

    // 📝 sync riga in tabella
    newItem: {
      handler(nv) {
        if (!nv.itemId) return;
        const row = this.formData.items.find((x) => x.itemId === nv.itemId);
        if (!row) return;
        const group = getCategoryGroup(this.selectedItem?.category);
        const isBoxBased = group === "area" || group === "linear";
        const qty = isBoxBased
          ? Number(nv.boxQuantity) || 0
          : Number(nv.pieces) || 0;
        // qty 0 = articolo appena riselezionato nel form (campo quantità ancora
        // vuoto): non azzerare la riga già presente in tabella
        if (qty > 0) {
          row.quantity = qty;
          row.boxQuantity = isBoxBased ? qty : 0;
          row.pieces = isBoxBased ? 0 : qty;
          if (isBoxBased) row.mqQuantity = this.typedMqQuantity();
        }
        row.price = nv.price;
        row.iva = nv.iva;
      },
      deep: true,
    },
  },
};
</script>

<style src="@/css/CreateQuotePage.css"></style>
